/**
 * Penalty level editor (admin only). Renders into a mount element.
 */
import {
    fetchPenaltyLevels,
    saveAdminPenaltyLevel,
    fetchPenaltyEditorDrafts,
    fetchPenaltyEditorDraft,
    savePenaltyEditorDraft,
    type PenaltyEditorDraftSummary,
} from "../lib/cloud";
import type {
    PenaltyLevelDef,
    WallSegDef,
    MovingLaserDef,
    SpeedrunToggleGroup,
    SpeedrunPointType,
    OscPathMove,
    SpeedrunWindDir,
    SpeedrunPortalDef,
    SpeedrunPortalSide,
} from "./penalty-levels";
import {
    normalizePenaltyLevelData,
    normPointInWindZone,
    normPointInTextZone,
    speedrunWindDirUnit,
    wallDrawAlpha,
    wallSegToPx,
    distPointToSeg,
    speedrunGroupRgb,
    movingLaserSegNorm,
    speedrunPointGoalPosNorm,
    speedrunPointTypeOf,
    drawSpeedrunPointShape,
    drawSpeedrunCheckpointDeltoid,
    speedrunPortalColor,
    speedrunLevelCheckpoints,
    speedrunLaserHalfPxForWall,
    speedrunLaserHalfPxForMoving,
    speedrunWindStrengthValue,
    drawSpeedrunTextLabel,
    speedrunTextRotateHandlePx,
    speedrunRotateWheelHandlePx,
    speedrunCheckpointRadiusPx,
} from "./penalty-levels";
import {
    drawSpeedrunPortals,
    resetSpeedrunPortalFx,
    updateSpeedrunPortalFx,
} from "./speedrun-portal-fx";
import { createModalCloseButton } from "./modal-close-button";

function positionAdminDropdownMenu(menu: HTMLElement, btn: HTMLElement) {
    const open = !menu.classList.contains("hidden");
    menu.classList.remove("adminToolDropdownMenuFixed");
    menu.style.left = "";
    menu.style.top = "";
    menu.style.maxHeight = "";
    menu.style.width = "";
    if (!open) return;
    const mobile = window.matchMedia("(max-width:780px)").matches;
    if (!mobile) return;
    menu.classList.add("adminToolDropdownMenuFixed");
    const r = btn.getBoundingClientRect();
    const pad = 8;
    const maxH = Math.max(120, window.innerHeight - r.bottom - pad - 12);
    menu.style.left = `${Math.max(pad, Math.min(r.left, window.innerWidth - 220 - pad))}px`;
    menu.style.top = `${r.bottom + 4}px`;
    menu.style.maxHeight = `${maxH}px`;
    menu.style.width = `${Math.min(r.width + 8, 220)}px`;
}

function clamp(v: number, a: number, b: number) {
    return Math.max(a, Math.min(b, v));
}

function uid() {
    return `w_${Math.random().toString(36).slice(2, 11)}`;
}

function distPointSegNorm(px: number, py: number, s: WallSegDef): number {
    const w = 1;
    const h = 1;
    const p = wallSegToPx(s, 0, w, h);
    const dx = p.x2 - p.x1;
    const dy = p.y2 - p.y1;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-12) return Math.hypot(px - s.ax, py - s.ay);
    let t = ((px - s.ax) * dx + (py - s.ay) * dy) / len2;
    t = clamp(t, 0, 1);
    const qx = s.ax + t * (s.bx - s.ax);
    const qy = s.ay + t * (s.by - s.ay);
    return Math.hypot(px - qx, py - qy);
}

function deepCloneDef(d: PenaltyLevelDef): PenaltyLevelDef {
    return normalizePenaltyLevelData(JSON.parse(JSON.stringify(d)));
}

function levelToJson(def: PenaltyLevelDef): object {
    return {
        spawn: def.spawn,
        ballSpawn: def.ballSpawn,
        goal: def.goal,
        staticSegs: def.staticSegs,
        moving: def.moving,
        points: def.points && def.points.length ? def.points : undefined,
        winds: def.winds && def.winds.length ? def.winds : undefined,
        texts: def.texts && def.texts.length ? def.texts : undefined,
        ...(def.checkpoints?.length ? { checkpoints: def.checkpoints } : {}),
        ...(def.temporalSpawn ? { temporalSpawn: def.temporalSpawn } : {}),
        ...(def.portals?.length ? { portals: def.portals } : {}),
    };
}

function ensureCheckpoints(def: PenaltyLevelDef) {
    if (!def.checkpoints) def.checkpoints = [];
    return def.checkpoints;
}

function ckKey(i: number) {
    return `ck:${i}`;
}

function parseCkKey(k: string): number | null {
    if (!k.startsWith("ck:")) return null;
    const i = Number(k.slice(3));
    return Number.isFinite(i) ? i : null;
}

function parsePortalKey(k: string): number | null {
    if (!k.startsWith("portal:")) return null;
    const i = Number(k.slice(7));
    return Number.isFinite(i) ? i : null;
}

function portalKey(i: number) {
    return `portal:${i}`;
}

export type AdminPenaltyEditorOptions = {
    onLevelsChanged: () => void;
    /** @default "admin" — "sandbox" hides server Publish and uses a different Play event. */
    variant?: "admin" | "sandbox";
    permissions?: {
        publish?: boolean;
        overwrite?: boolean;
    };
};

export function mountAdminPenaltyEditor(mount: HTMLElement, opts: AdminPenaltyEditorOptions) {
    mount.replaceChildren();

    const root = document.createElement("div");
    root.className = "adminPenaltyRoot";

    const toolbar = document.createElement("div");
    toolbar.className = "adminPenaltyToolbar";

    const mkBtn = (label: string, onClick: () => void) => {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "btn";
        b.textContent = label;
        b.onclick = onClick;
        return b;
    };

    const TOOL_GROUPS: { heading: string; tools: { id: string; label: string }[] }[] = [
        {
            heading: "Spawn & goal",
            tools: [
                { id: "spawn", label: "Move cursor spawn" },
                { id: "ballSpawn", label: "Move ball spawn" },
                { id: "goal", label: "Move Goal" },
            ],
        },
        {
            heading: "Lasers",
            tools: [
                { id: "draw", label: "Draw laser" },
                { id: "paint", label: "Paint laser" },
                { id: "square", label: "Square laser" },
                { id: "circle", label: "Circle laser" },
            ],
        },
        {
            heading: "Walls",
            tools: [
                { id: "drawWall", label: "Draw walls" },
                { id: "paintWall", label: "Paint walls" },
                { id: "squareWall", label: "Square walls" },
                { id: "circleWall", label: "Circle walls" },
            ],
        },
        {
            heading: "Other Objects",
            tools: [
                { id: "points", label: "Add Points" },
                { id: "checkpoint", label: "Checkpoint" },
                { id: "portal", label: "Portals" },
                { id: "wind", label: "Wind" },
                { id: "text", label: "Text" },
            ],
        },
        {
            heading: "Edit",
            tools: [
                { id: "select", label: "Select" },
                { id: "erase", label: "Eraser" },
            ],
        },
    ];
    const ALL_TOOL_OPTIONS = TOOL_GROUPS.flatMap((g) => g.tools);

    const LASER_STROKE_TOOLS = new Set(["draw", "paint", "square", "circle"]);
    const SOLID_WALL_STROKE_TOOLS = new Set(["drawWall", "paintWall", "squareWall", "circleWall"]);
    const STROKE_WIDTH_TOOLS = new Set([...LASER_STROKE_TOOLS, ...SOLID_WALL_STROKE_TOOLS]);

    let activeTool = "spawn";
    const row1 = document.createElement("div");
    row1.className = "adminToolRow adminToolDropdownRow";
    const deleteObjectBtn = document.createElement("button");
    deleteObjectBtn.type = "button";
    deleteObjectBtn.className = "btn adminDeleteObjectBtn hidden";
    deleteObjectBtn.textContent = "Delete Object";
    row1.appendChild(deleteObjectBtn);
    const temporalSpawnBtn = mkBtn("Temporal Spawn", () => {
        activeTool = "temporalSpawn";
        syncToolDropdown();
    });
    const toolDropdownWrap = document.createElement("div");
    toolDropdownWrap.className = "adminToolDropdown";
    const toolDropdownBtn = document.createElement("button");
    toolDropdownBtn.type = "button";
    toolDropdownBtn.className = "btn adminToolDropdownBtn";
    const toolDropdownMenu = document.createElement("div");
    toolDropdownMenu.className = "adminToolDropdownMenu hidden";
    function syncToolDropdown() {
        const cur = ALL_TOOL_OPTIONS.find((o) => o.id === activeTool);
        toolDropdownBtn.textContent = `${cur ? cur.label : "Tool"} ▾`;
        for (const ch of toolDropdownMenu.querySelectorAll("[data-tool-id]")) {
            const el = ch as HTMLElement;
            el.classList.toggle("green", el.dataset.toolId === activeTool);
        }
        syncPointsOptsRow();
        syncPortalOptsRow();
        syncLaserWidthRow();
        syncAnimPanelVisibility();
        temporalSpawnBtn.classList.toggle("green", activeTool === "temporalSpawn");
        if (activeTool === "portal") syncPortalPickersFromSelection();
    }
    function applyToolChoice(id: string) {
        activeTool = id;
        circleStart = null;
        squareStart = null;
        windSquareStart = null;
        textSquareStart = null;
        selectionRotateMode = false;
        textRotateBtn.classList.remove("green");
        objectRotateBtn.classList.remove("green");
        dragLineStart = null;
        portalDragStart = null;
        selectMarqueeAnchor = null;
        selectMarqueePick = null;
        selectPointerMode = null;
        syncToolDropdown();
        toolDropdownMenu.classList.add("hidden");
    }
    for (const grp of TOOL_GROUPS) {
        const gh = document.createElement("div");
        gh.className = "adminToolDropdownHeading";
        gh.textContent = grp.heading;
        toolDropdownMenu.appendChild(gh);
        for (const opt of grp.tools) {
            const it = document.createElement("button");
            it.type = "button";
            it.className = "btn adminToolDropdownItem";
            it.textContent = opt.label;
            it.dataset.toolId = opt.id;
            it.onclick = (ev) => {
                ev.stopPropagation();
                applyToolChoice(opt.id);
            };
            toolDropdownMenu.appendChild(it);
        }
    }
    toolDropdownBtn.onclick = (ev) => {
        ev.stopPropagation();
        toolDropdownMenu.classList.toggle("hidden");
        positionAdminDropdownMenu(toolDropdownMenu, toolDropdownBtn);
    };
    let closeToolDropdown: ((ev: MouseEvent) => void) | null = (ev: MouseEvent) => {
        const t = ev.target as Node | null;
        if (t && !toolDropdownWrap.contains(t)) {
            toolDropdownMenu.classList.add("hidden");
            positionAdminDropdownMenu(toolDropdownMenu, toolDropdownBtn);
        }
    };
    document.addEventListener("click", closeToolDropdown, true);
    toolDropdownWrap.appendChild(toolDropdownBtn);
    toolDropdownWrap.appendChild(toolDropdownMenu);
    row1.appendChild(toolDropdownWrap);

    const rowPointsOpts = document.createElement("div");
    rowPointsOpts.className = "adminToolRow adminPointsOptsRow hidden";
    const toggleWallsCheck = document.createElement("input");
    toggleWallsCheck.type = "checkbox";
    const toggleWallsLab = document.createElement("label");
    toggleWallsLab.className = "adminCheck";
    toggleWallsLab.appendChild(toggleWallsCheck);
    toggleWallsLab.appendChild(document.createTextNode("Toggle laser"));
    const pointsGroupSelect = document.createElement("select");
    pointsGroupSelect.className = "formInput adminPointsGroupSelect";
    const o0 = document.createElement("option");
    o0.value = "";
    o0.textContent = "Default yellow";
    pointsGroupSelect.appendChild(o0);
    for (let g = 1; g <= 15; g++) {
        const o = document.createElement("option");
        o.value = String(g);
        o.textContent = `Group ${g}`;
        pointsGroupSelect.appendChild(o);
    }
    rowPointsOpts.appendChild(toggleWallsLab);
    rowPointsOpts.appendChild(pointsGroupSelect);
    const pointsTypeLabel = document.createElement("span");
    pointsTypeLabel.className = "accessHint";
    pointsTypeLabel.style.marginLeft = "8px";
    pointsTypeLabel.textContent = "Point Type";
    const pointsTypeSelect = document.createElement("select");
    pointsTypeSelect.className = "formInput adminPointsTypeSelect";
    for (const [val, label] of [
        ["circle", "Circle"],
        ["triangle", "Triangle"],
        ["square", "Square"],
    ] as const) {
        const o = document.createElement("option");
        o.value = val;
        o.textContent = label;
        pointsTypeSelect.appendChild(o);
    }
    rowPointsOpts.appendChild(pointsTypeLabel);
    rowPointsOpts.appendChild(pointsTypeSelect);
    function readPointTypeSelect(sel: HTMLSelectElement): SpeedrunPointType {
        const v = sel.value;
        if (v === "triangle" || v === "square") return v;
        return "circle";
    }
    function applyTogglePointType(target: { togglePointType?: SpeedrunPointType }, ptType: SpeedrunPointType) {
        if (ptType === "circle") delete target.togglePointType;
        else target.togglePointType = ptType;
    }
    function syncPointsOptsRow() {
        rowPointsOpts.classList.toggle("hidden", activeTool !== "points");
    }

    const rowPortalOpts = document.createElement("div");
    rowPortalOpts.className = "adminToolRow adminPortalOptsRow hidden";

    let portalGroupPick = 1;
    let portalSidePick: SpeedrunPortalSide = "a";

    const mkPortalPicker = (label: string, options: { value: string; text: string }[], onPick: (v: string) => void) => {
        const wrap = document.createElement("div");
        wrap.className = "adminToolDropdown adminPortalPickerDropdown";
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "btn adminToolDropdownBtn";
        const menu = document.createElement("div");
        menu.className = "adminToolDropdownMenu hidden";
        let current = options[0]?.value ?? "";
        const syncBtn = () => {
            const cur = options.find((o) => o.value === current);
            btn.textContent = `${label}: ${cur?.text ?? current} ▾`;
        };
        for (const opt of options) {
            const it = document.createElement("button");
            it.type = "button";
            it.className = "btn adminToolDropdownItem";
            it.textContent = opt.text;
            it.onclick = (ev) => {
                ev.stopPropagation();
                current = opt.value;
                syncBtn();
                menu.classList.add("hidden");
                onPick(opt.value);
            };
            menu.appendChild(it);
        }
        btn.onclick = (ev) => {
            ev.stopPropagation();
            menu.classList.toggle("hidden");
            positionAdminDropdownMenu(menu, btn);
        };
        document.addEventListener("click", (ev) => {
            const t = ev.target as Node | null;
            if (t && !wrap.contains(t)) {
                menu.classList.add("hidden");
                positionAdminDropdownMenu(menu, btn);
            }
        });
        syncBtn();
        wrap.appendChild(btn);
        wrap.appendChild(menu);
        return { wrap, get: () => current, set: (v: string) => {
            current = v;
            syncBtn();
        } };
    };

    const portalGroupOptions = Array.from({ length: 10 }, (_, i) => ({
        value: String(i + 1),
        text: String(i + 1),
    }));
    const portalSideOptions = [
        { value: "a", text: "A" },
        { value: "b", text: "B" },
    ];
    const portalGroupPicker = mkPortalPicker("Group", portalGroupOptions, (v) => {
        portalGroupPick = clamp(Math.round(Number(v) || 1), 1, 10);
    });
    const portalSidePicker = mkPortalPicker("Side", portalSideOptions, (v) => {
        portalSidePick = v === "b" ? "b" : "a";
    });
    rowPortalOpts.appendChild(portalGroupPicker.wrap);
    rowPortalOpts.appendChild(portalSidePicker.wrap);

    function syncPortalOptsRow() {
        rowPortalOpts.classList.toggle("hidden", activeTool !== "portal");
    }

    function readPortalGroupPick() {
        return clamp(Math.round(Number(portalGroupPicker.get()) || portalGroupPick), 1, 10);
    }

    function readPortalSidePick(): SpeedrunPortalSide {
        return portalSidePicker.get() === "b" ? "b" : "a";
    }

    function upsertPortalLine(group: number, side: SpeedrunPortalSide, ax: number, ay: number, bx: number, by: number) {
        if (!def.portals) def.portals = [];
        const portal: SpeedrunPortalDef = {
            id: uid(),
            group,
            side,
            ax: clamp(ax, 0, 1),
            ay: clamp(ay, 0, 1),
            bx: clamp(bx, 0, 1),
            by: clamp(by, 0, 1),
        };
        const idx = def.portals.findIndex((p) => p.group === group && p.side === side);
        if (idx >= 0) {
            def.portals[idx] = portal;
            return idx;
        }
        def.portals.push(portal);
        return def.portals.length - 1;
    }

    function syncPortalPickersFromSelection() {
        if (selectedKeys.size !== 1) return;
        const k = [...selectedKeys][0];
        const pi = parsePortalKey(k);
        if (pi == null || !def.portals?.[pi]) return;
        const p = def.portals[pi];
        portalGroupPicker.set(String(p.group));
        portalSidePicker.set(p.side);
        portalGroupPick = p.group;
        portalSidePick = p.side;
    }

    const rowSelectLaserOpts = document.createElement("div");
    rowSelectLaserOpts.className = "adminToolRow adminSelectLaserOptsRow hidden";
    const selectLaserGroupLabel = document.createElement("span");
    selectLaserGroupLabel.className = "accessHint";
    selectLaserGroupLabel.style.marginRight = "6px";
    selectLaserGroupLabel.textContent = "Group";
    const selectLaserGroupSelect = document.createElement("select");
    selectLaserGroupSelect.className = "formInput adminSelectLaserGroupSelect";
    const selG0 = document.createElement("option");
    selG0.value = "";
    selG0.textContent = "None";
    selectLaserGroupSelect.appendChild(selG0);
    for (let g = 1; g <= 15; g++) {
        const o = document.createElement("option");
        o.value = String(g);
        o.textContent = `Group ${g}`;
        selectLaserGroupSelect.appendChild(o);
    }
    rowSelectLaserOpts.appendChild(selectLaserGroupLabel);
    rowSelectLaserOpts.appendChild(selectLaserGroupSelect);
    const selectLaserPointTypeLabel = document.createElement("span");
    selectLaserPointTypeLabel.className = "accessHint";
    selectLaserPointTypeLabel.style.marginLeft = "8px";
    selectLaserPointTypeLabel.textContent = "Point Type";
    const selectLaserPointTypeSelect = document.createElement("select");
    selectLaserPointTypeSelect.className = "formInput adminSelectLaserPointTypeSelect";
    for (const [val, label] of [
        ["circle", "Circle"],
        ["triangle", "Triangle"],
        ["square", "Square"],
    ] as const) {
        const o = document.createElement("option");
        o.value = val;
        o.textContent = label;
        selectLaserPointTypeSelect.appendChild(o);
    }
    rowSelectLaserOpts.appendChild(selectLaserPointTypeLabel);
    rowSelectLaserOpts.appendChild(selectLaserPointTypeSelect);

    /** Thickness (`lineHalfMul`) for new strokes with laser tools; below 1 = thinner than default. */
    let laserDrawWidthMul = 0.35;
    const rowLaserDrawWidth = document.createElement("div");
    rowLaserDrawWidth.className = "adminToolRow adminLaserWidthRow hidden";
    const laserWidthLabel = document.createElement("span");
    laserWidthLabel.className = "accessHint";
    laserWidthLabel.style.marginRight = "6px";
    laserWidthLabel.textContent = "Width";
    const laserWidthSlider = document.createElement("input");
    laserWidthSlider.type = "range";
    laserWidthSlider.className = "formInput";
    laserWidthSlider.min = "0.25";
    laserWidthSlider.max = "5";
    laserWidthSlider.step = "0.05";
    laserWidthSlider.value = String(laserDrawWidthMul);
    laserWidthSlider.style.flex = "1";
    laserWidthSlider.style.minWidth = "80px";
    const laserWidthNum = document.createElement("input");
    laserWidthNum.type = "number";
    laserWidthNum.className = "formInput";
    laserWidthNum.min = "0.25";
    laserWidthNum.max = "5";
    laserWidthNum.step = "0.05";
    laserWidthNum.style.maxWidth = "72px";
    rowLaserDrawWidth.appendChild(laserWidthLabel);
    rowLaserDrawWidth.appendChild(laserWidthSlider);
    rowLaserDrawWidth.appendChild(laserWidthNum);
    let laserWidthUiSyncing = false;
    function syncLaserWidthRow() {
        const showLaserWidthTool = STROKE_WIDTH_TOOLS.has(activeTool);
        rowLaserDrawWidth.classList.toggle("hidden", !showLaserWidthTool);
        if (!showLaserWidthTool) return;
        laserWidthUiSyncing = true;
        const m = clamp(laserDrawWidthMul, 0.25, 5);
        laserWidthSlider.value = String(m);
        laserWidthNum.value = Number.isInteger(m) ? String(m) : m.toFixed(2);
        laserWidthUiSyncing = false;
    }
    laserWidthSlider.addEventListener("input", () => {
        if (laserWidthUiSyncing) return;
        laserDrawWidthMul = clamp(Number(laserWidthSlider.value), 0.25, 5);
        laserWidthUiSyncing = true;
        laserWidthNum.value = Number.isInteger(laserDrawWidthMul)
            ? String(laserDrawWidthMul)
            : laserDrawWidthMul.toFixed(2);
        laserWidthUiSyncing = false;
        paint();
    });
    laserWidthNum.addEventListener("change", () => {
        if (laserWidthUiSyncing) return;
        let v = Number(laserWidthNum.value);
        if (!Number.isFinite(v)) {
            syncLaserWidthRow();
            return;
        }
        laserDrawWidthMul = clamp(v, 0.25, 5);
        syncLaserWidthRow();
        paint();
    });

    const rowEntitySize = document.createElement("div");
    rowEntitySize.className = "adminToolRow adminEntitySizeRow hidden";
    const entitySizeLabel = document.createElement("span");
    entitySizeLabel.className = "accessHint";
    entitySizeLabel.style.marginRight = "6px";
    entitySizeLabel.textContent = "Size";
    const entitySizeSlider = document.createElement("input");
    entitySizeSlider.type = "range";
    entitySizeSlider.className = "formInput";
    entitySizeSlider.id = "admEntitySizeSlider";
    entitySizeSlider.style.flex = "1";
    entitySizeSlider.style.minWidth = "80px";
    const entitySizeNum = document.createElement("input");
    entitySizeNum.type = "number";
    entitySizeNum.className = "formInput";
    entitySizeNum.id = "admEntitySizeNum";
    entitySizeNum.style.maxWidth = "88px";
    rowEntitySize.appendChild(entitySizeLabel);
    rowEntitySize.appendChild(entitySizeSlider);
    rowEntitySize.appendChild(entitySizeNum);

    const rowObjectRotate = document.createElement("div");
    rowObjectRotate.className = "adminToolRow adminObjectRotateRow hidden";
    const objectRotateBtn = mkBtn("Rotate", () => {
        if (!hasRotatableSelection()) return;
        selectionRotateMode = !selectionRotateMode;
        objectRotateBtn.classList.toggle("green", selectionRotateMode);
        textRotateBtn.classList.toggle("green", selectionRotateMode);
        paint();
    });
    rowObjectRotate.appendChild(objectRotateBtn);

    const rowCheck = document.createElement("div");
    rowCheck.className = "adminToolRow adminToolRowCheck";

    let moveWallsEnabled = false;
    let rotateWallsEnabled = false;

    const moveWallCheck = document.createElement("input");
    moveWallCheck.type = "checkbox";
    const moveWallLab = document.createElement("label");
    moveWallLab.className = "adminCheck";
    moveWallLab.appendChild(moveWallCheck);
    moveWallLab.appendChild(document.createTextNode("Move object"));
    let syncAnimPanelVisibility: () => void = () => {};

    moveWallCheck.onchange = () => {
        moveWallsEnabled = moveWallCheck.checked;
        if (moveWallsEnabled) {
            rotateWallsEnabled = false;
            rotWallCheck.checked = false;
        } else {
            movePathDraft = null;
            pendingMovePick = null;
            syncPathDraftUi();
        }
        syncAnimPanelVisibility();
    };

    const rotWallCheck = document.createElement("input");
    rotWallCheck.type = "checkbox";
    const rotWallLab = document.createElement("label");
    rotWallLab.className = "adminCheck";
    rotWallLab.appendChild(rotWallCheck);
    rotWallLab.appendChild(document.createTextNode("Rotate object"));
    rotWallCheck.onchange = () => {
        rotateWallsEnabled = rotWallCheck.checked;
        if (rotateWallsEnabled) {
            moveWallsEnabled = false;
            moveWallCheck.checked = false;
            movePathDraft = null;
            pendingMovePick = null;
            syncPathDraftUi();
            if (activeTool === "select") {
                const fi = [...selectedKeys].find((k) => k.startsWith("seg:"));
                if (fi) {
                    const idx = Number(fi.slice(4));
                    if (def.staticSegs[idx]) {
                        selectedSeg = idx;
                        loadRotUiFromSeg(def.staticSegs[idx]);
                    }
                }
            }
        }
        syncAnimPanelVisibility();
    };

    const pauseAnimationsCheck = document.createElement("input");
    pauseAnimationsCheck.type = "checkbox";
    const pauseAnimLab = document.createElement("label");
    pauseAnimLab.className = "adminCheck";
    pauseAnimLab.appendChild(pauseAnimationsCheck);
    pauseAnimLab.appendChild(document.createTextNode("Pause animations"));

    const hidePathCheck = document.createElement("input");
    hidePathCheck.type = "checkbox";
    hidePathCheck.id = "admHidePath";
    const hidePathLab = document.createElement("label");
    hidePathLab.className = "adminCheck";
    hidePathLab.appendChild(hidePathCheck);
    hidePathLab.appendChild(document.createTextNode("Hide path"));

    rowCheck.appendChild(moveWallLab);
    rowCheck.appendChild(rotWallLab);
    rowCheck.appendChild(pauseAnimLab);
    rowCheck.appendChild(hidePathLab);
    hidePathCheck.onchange = () => paint();

    const rowUndo = document.createElement("div");
    rowUndo.className = "adminToolRow";
    const undoBtn = mkBtn("Undo", () => {});
    const redoBtn = mkBtn("Redo", () => {});
    rowUndo.appendChild(undoBtn);
    rowUndo.appendChild(redoBtn);

    const rowDeleteBulk = document.createElement("div");
    rowDeleteBulk.className = "adminToolRow adminDeleteBulkRow";

    const rowAnim = document.createElement("div");
    rowAnim.className = "adminAnimPanel modeBox";
    const admAnimTitle = document.createElement("div");
    admAnimTitle.className = "adminAnimTitle";
    admAnimTitle.textContent = "Laser motion (select a segment on canvas first)";
    const admMoveFields = document.createElement("div");
    admMoveFields.className = "adminAnimMoveFields";
    const mkFormRow = (label: string, el: HTMLElement) => {
        const r = document.createElement("div");
        r.className = "formRow";
        const sp = document.createElement("span");
        sp.className = "formLabel";
        sp.textContent = label;
        r.appendChild(sp);
        r.appendChild(el);
        return r;
    };
    const admMoveOut = document.createElement("input");
    admMoveOut.type = "number";
    admMoveOut.className = "formInput";
    admMoveOut.id = "admMoveOut";
    admMoveOut.value = "1.2";
    admMoveOut.step = "0.1";
    admMoveOut.min = "0.05";
    const admMoveBack = document.createElement("input");
    admMoveBack.type = "number";
    admMoveBack.className = "formInput";
    admMoveBack.id = "admMoveBack";
    admMoveBack.value = "1.2";
    admMoveBack.step = "0.1";
    admMoveBack.min = "0.05";
    admMoveFields.appendChild(mkFormRow("Move out (s)", admMoveOut));
    admMoveFields.appendChild(mkFormRow("Move back (s)", admMoveBack));

    const pathDoneBtn = document.createElement("button");
    pathDoneBtn.type = "button";
    pathDoneBtn.className = "btn green";
    pathDoneBtn.textContent = "Done";
    pathDoneBtn.classList.add("hidden");
    const pathLoopRow = document.createElement("div");
    pathLoopRow.className = "formRow adminPathLoopRow hidden";
    const pathLoopCheck = document.createElement("input");
    pathLoopCheck.type = "checkbox";
    pathLoopCheck.id = "admPathLoop";
    const pathLoopLab = document.createElement("label");
    pathLoopLab.className = "adminCheck";
    pathLoopLab.appendChild(pathLoopCheck);
    pathLoopLab.appendChild(document.createTextNode("Loop"));
    pathLoopRow.appendChild(pathLoopLab);
    admMoveFields.appendChild(pathDoneBtn);
    admMoveFields.appendChild(pathLoopRow);

    const admRotateFields = document.createElement("div");
    admRotateFields.className = "adminAnimRotateFields";
    const admRotDegSlider = document.createElement("input");
    admRotDegSlider.type = "range";
    admRotDegSlider.className = "formInput";
    admRotDegSlider.id = "admRotDegSlider";
    admRotDegSlider.min = "-180";
    admRotDegSlider.max = "180";
    admRotDegSlider.value = "90";
    const admRotDeg = document.createElement("input");
    admRotDeg.type = "number";
    admRotDeg.className = "formInput";
    admRotDeg.id = "admRotDeg";
    admRotDeg.value = "90";
    admRotDeg.step = "1";
    admRotDeg.style.maxWidth = "88px";
    const rotDegRow = document.createElement("div");
    rotDegRow.className = "formRow";
    const rotDegLab = document.createElement("span");
    rotDegLab.className = "formLabel";
    rotDegLab.textContent = "Rotate °";
    rotDegRow.appendChild(rotDegLab);
    rotDegRow.appendChild(admRotDegSlider);
    rotDegRow.appendChild(admRotDeg);
    const admRotPeriod = document.createElement("input");
    admRotPeriod.type = "number";
    admRotPeriod.className = "formInput";
    admRotPeriod.id = "admRotPeriod";
    admRotPeriod.value = "3";
    admRotPeriod.step = "0.1";
    admRotPeriod.min = "0.05";
    admRotateFields.appendChild(rotDegRow);
    admRotateFields.appendChild(mkFormRow("Rotate period (s)", admRotPeriod));
    const admRotInfRow = document.createElement("div");
    admRotInfRow.className = "formRow adminRotInfRow";
    const admRotInfinite = document.createElement("input");
    admRotInfinite.type = "checkbox";
    admRotInfinite.id = "admRotInfinite";
    const admRotInfLab = document.createElement("label");
    admRotInfLab.className = "adminCheck";
    admRotInfLab.appendChild(admRotInfinite);
    admRotInfLab.appendChild(document.createTextNode("Infinite"));
    admRotInfRow.appendChild(admRotInfLab);
    const admRotDirRow = document.createElement("div");
    admRotDirRow.className = "formRow adminRotDirRow hidden";
    const admRotRight = document.createElement("input");
    admRotRight.type = "checkbox";
    admRotRight.id = "admRotRight";
    const admRotRightLab = document.createElement("label");
    admRotRightLab.className = "adminCheck";
    admRotRightLab.appendChild(admRotRight);
    admRotRightLab.appendChild(document.createTextNode("Right"));
    const admRotLeft = document.createElement("input");
    admRotLeft.type = "checkbox";
    admRotLeft.id = "admRotLeft";
    const admRotLeftLab = document.createElement("label");
    admRotLeftLab.className = "adminCheck";
    admRotLeftLab.appendChild(admRotLeft);
    admRotLeftLab.appendChild(document.createTextNode("Left"));
    admRotDirRow.appendChild(admRotRightLab);
    admRotDirRow.appendChild(admRotLeftLab);
    admRotateFields.appendChild(admRotInfRow);
    admRotateFields.appendChild(admRotDirRow);
    const admHint = document.createElement("p");
    admHint.className = "accessHint";
    admHint.textContent =
        "Move object: with Select and a selection, each empty click adds a vertex to the group centroid path; Done commits (≥2 vertices: Loop = return to start and repeat; without Loop = return along the same path). Move out/back = seconds per leg. Rotate: pick a segment and use the fields.";
    rowAnim.appendChild(admAnimTitle);
    rowAnim.appendChild(admMoveFields);
    rowAnim.appendChild(admRotateFields);
    rowAnim.appendChild(admHint);

    function syncAnimPanelTitle() {
        const speedEdit = activeTool === "select" && getMoveSpeedEditTarget() !== null;
        if (rotateWallsEnabled) {
            admAnimTitle.textContent =
                activeTool === "select" && [...selectedKeys].some((k) => k.startsWith("seg:"))
                    ? "Rotation (selected segments)"
                    : "Wall rotation (pick a segment)";
        } else if (moveWallsEnabled) {
            admAnimTitle.textContent =
                activeTool === "select" && selectedKeys.size > 0
                    ? "Motion path (Select → empty clicks → Done)"
                    : "Motion path (pick a segment)";
        } else if (speedEdit) {
            admAnimTitle.textContent = "Motion speed (selected segment)";
        } else {
            admAnimTitle.textContent = "Motion & rotation";
        }
    }

    function syncAnimPanelVisibilityImpl() {
        const speedEdit = activeTool === "select" && getMoveSpeedEditTarget() !== null;
        const hasSelSegs = [...selectedKeys].some((k) => k.startsWith("seg:"));
        const selMoveReady = activeTool === "select" && selectedKeys.size > 0;
        const showPanel = moveWallsEnabled || rotateWallsEnabled || speedEdit;
        rowAnim.classList.toggle("hidden", !showPanel);
        const showMoveFields = moveWallsEnabled || speedEdit;
        admMoveFields.classList.toggle("hidden", !showMoveFields);
        const showRotFields = rotateWallsEnabled;
        admRotateFields.classList.toggle("hidden", !showRotFields);
        if (rotateWallsEnabled && hasSelSegs) {
            const fi = Number([...selectedKeys].find((k) => k.startsWith("seg:"))!.slice(4));
            if (def.staticSegs[fi]) loadRotUiFromSeg(def.staticSegs[fi]);
        }
        syncAnimPanelTitle();
    }
    syncAnimPanelVisibility = syncAnimPanelVisibilityImpl;

    const row2 = document.createElement("div");
    row2.className = "adminToolRow adminFileActionsRow";
    const isSandboxEditor = opts.variant === "sandbox";
    const canPublish = !!opts.permissions?.publish;
    const canOverwrite = !!opts.permissions?.overwrite;

    const mkSection = (title: string) => {
        const sec = document.createElement("section");
        sec.className = "adminToolbarSection";
        const h = document.createElement("div");
        h.className = "adminToolbarSectionTitle";
        h.textContent = title;
        sec.appendChild(h);
        const body = document.createElement("div");
        body.className = "adminToolbarSectionBody";
        sec.appendChild(body);
        return { sec, body };
    };

    const fileSec = mkSection("Level");
    const editSec = mkSection("Edit");
    const playSec = mkSection("Test");

    const runPlayTest = () => {
        document.dispatchEvent(
            new CustomEvent(isSandboxEditor ? "arcade-sandbox-penalty-editor-play" : "arcade-admin-penalty-play", {
                detail: {
                    def: deepCloneDef(def),
                    pauseAnimations: pauseAnimationsCheck.checked,
                },
            }),
        );
    };

    const playTestBtn = mkBtn("Play", () => runPlayTest());
    playSec.body.appendChild(playTestBtn);

    const saveDraftBtn = mkBtn("Save", () => openSaveDraftDialog());
    const loadDraftBtn = mkBtn("Load", () => openLoadDraftDialog());
    const publishBtn = mkBtn("Publish", () => openPublishDialog());
    fileSec.body.append(saveDraftBtn, loadDraftBtn, publishBtn);
    if (isSandboxEditor) {
        saveDraftBtn.classList.add("hidden");
        loadDraftBtn.classList.add("hidden");
        publishBtn.classList.add("hidden");
    } else if (!canPublish && !canOverwrite) {
        publishBtn.disabled = true;
        publishBtn.title = "Requires Publish Penalty Levels permission";
    }

    const copyBtn = mkBtn("Copy", () => copySelection());
    const pasteBtn = mkBtn("Paste", () => pasteClipboard());
    editSec.body.append(copyBtn, pasteBtn);

    row2.append(fileSec.sec, editSec.sec, playSec.sec);

    const canvasWrap = document.createElement("div");
    canvasWrap.className = "adminCanvasWrap";
    const canvas = document.createElement("canvas");
    canvas.className = "adminPenaltyCanvas";
    canvas.width = 1120;
    canvas.height = 700;
    canvas.setAttribute("aria-label", "Penalty level editor");
    canvasWrap.appendChild(canvas);

    const windSide = document.createElement("aside");
    windSide.className = "adminWindSide hidden";
    windSide.setAttribute("aria-label", "Wind");
    const windPanelTitle = document.createElement("div");
    windPanelTitle.className = "adminWindPanelTitle";
    windPanelTitle.textContent = "Wind Direccion";
    const windDirGrid = document.createElement("div");
    windDirGrid.className = "adminWindDirGrid";
    type DirBtn = { dir: SpeedrunWindDir; el: HTMLButtonElement };
    const windDirButtons: DirBtn[] = [];
    const mkWindDirBtn = (dir: SpeedrunWindDir, sym: string) => {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "btn adminWindDirBtn";
        b.dataset.windDir = dir;
        b.textContent = sym;
        b.title = dir;
        windDirButtons.push({ dir, el: b });
        b.onclick = () => {
            if (windDirUiSyncing) return;
            applyWindDirToSelectedWind(dir);
            paint();
            maybeRecordHistory();
        };
        return b;
    };
    windDirGrid.appendChild(mkWindDirBtn("up", "↑"));
    windDirGrid.appendChild(mkWindDirBtn("left", "←"));
    windDirGrid.appendChild(mkWindDirBtn("down", "↓"));
    windDirGrid.appendChild(mkWindDirBtn("right", "→"));
    const windStrengthRow = document.createElement("div");
    windStrengthRow.className = "adminWindStrengthRow";
    const windStrengthLabel = document.createElement("span");
    windStrengthLabel.className = "accessHint adminWindStrengthLabel";
    windStrengthLabel.textContent = "Wind strength";
    const windStrengthSlider = document.createElement("input");
    windStrengthSlider.type = "range";
    windStrengthSlider.className = "adminWindStrengthSlider";
    windStrengthSlider.min = "0.15";
    windStrengthSlider.max = "50";
    windStrengthSlider.step = "0.05";
    const windStrengthNum = document.createElement("input");
    windStrengthNum.type = "number";
    windStrengthNum.className = "formInput adminWindStrengthNum";
    windStrengthNum.min = "0.15";
    windStrengthNum.max = "50";
    windStrengthNum.step = "0.05";
    windStrengthRow.appendChild(windStrengthLabel);
    windStrengthRow.appendChild(windStrengthSlider);
    windStrengthRow.appendChild(windStrengthNum);
    let windStrengthUiSyncing = false;
    function applyWindStrengthToSelectedWinds(v: number) {
        const sv = clamp(v, 0.15, 50);
        for (const k of selectedKeys) {
            if (!k.startsWith("wind:")) continue;
            const i = Number(k.slice(5));
            const w = def.winds?.[i];
            if (!w) continue;
            if (Math.abs(sv - 1) < 1e-5) delete w.strength;
            else w.strength = sv;
        }
    }
    windStrengthSlider.addEventListener("input", () => {
        if (windStrengthUiSyncing) return;
        let v = Number(windStrengthSlider.value);
        if (!Number.isFinite(v)) return;
        v = clamp(v, 0.15, 50);
        windStrengthUiSyncing = true;
        windStrengthNum.value = v.toFixed(2);
        windStrengthUiSyncing = false;
        applyWindStrengthToSelectedWinds(v);
        paint();
        scheduleWindStrengthHistory();
    });
    windStrengthNum.addEventListener("change", () => {
        if (windStrengthUiSyncing) return;
        let v = Number(windStrengthNum.value);
        if (!Number.isFinite(v)) {
            syncWindStrengthUiFromSelection();
            return;
        }
        v = clamp(v, 0.15, 50);
        windStrengthUiSyncing = true;
        windStrengthSlider.value = String(v);
        windStrengthNum.value = v.toFixed(2);
        windStrengthUiSyncing = false;
        applyWindStrengthToSelectedWinds(v);
        paint();
        maybeRecordHistory();
    });
    let windStrengthHistTimer: number | null = null;
    function scheduleWindStrengthHistory() {
        if (windStrengthHistTimer != null) window.clearTimeout(windStrengthHistTimer);
        windStrengthHistTimer = window.setTimeout(() => {
            windStrengthHistTimer = null;
            maybeRecordHistory();
        }, 350);
    }
    function syncWindStrengthUiFromSelection() {
        const windKeys = [...selectedKeys].filter((k) => k.startsWith("wind:"));
        if (!windKeys.length) return;
        windStrengthUiSyncing = true;
        let acc: number | null = null;
        let mixed = false;
        for (const k of windKeys) {
            const i = Number(k.slice(5));
            const w = def.winds?.[i];
            const sv = w ? speedrunWindStrengthValue(w) : 1;
            if (acc == null) acc = sv;
            else if (Math.abs(sv - acc) > 1e-4) mixed = true;
        }
        const showV = mixed ? 1 : acc ?? 1;
        windStrengthSlider.value = String(showV);
        windStrengthNum.value = mixed ? "" : (acc ?? 1).toFixed(2);
        if (mixed) windStrengthNum.placeholder = "—";
        else windStrengthNum.removeAttribute("placeholder");
        windStrengthUiSyncing = false;
    }
    const windDoneBtn = mkBtn("Done", () => {
        maybeRecordHistory();
        toolDropdownMenu.classList.add("hidden");
    });
    windDoneBtn.classList.add("adminWindDoneBtn");
    windSide.appendChild(windPanelTitle);
    windSide.appendChild(windDirGrid);
    windSide.appendChild(windStrengthRow);
    windSide.appendChild(windDoneBtn);

    let windDirUiSyncing = false;
    function getSingleSelectedWindIndex(): number | null {
        if (activeTool !== "select" || selectedKeys.size !== 1) return null;
        const k = [...selectedKeys][0];
        if (!k.startsWith("wind:")) return null;
        const i = Number(k.slice(5));
        return Number.isFinite(i) && def.winds?.[i] ? i : null;
    }
    function applyWindDirToSelectedWind(dir: SpeedrunWindDir) {
        const idx = getSingleSelectedWindIndex();
        if (idx != null) {
            def.winds![idx].dir = dir;
            return;
        }
        for (const k of selectedKeys) {
            if (!k.startsWith("wind:")) continue;
            const i = Number(k.slice(5));
            const w = def.winds?.[i];
            if (w) w.dir = dir;
        }
    }
    function syncWindDirPanelFromSelection() {
        const show =
            activeTool === "select" && [...selectedKeys].some((k) => k.startsWith("wind:"));
        windSide.classList.toggle("hidden", !show);
        if (!show) return;
        windDirUiSyncing = true;
        let activeDir: SpeedrunWindDir | null = null;
        const windKeys = [...selectedKeys].filter((k) => k.startsWith("wind:"));
        for (const k of windKeys) {
            const i = Number(k.slice(5));
            const w = def.winds?.[i];
            const d = w?.dir ?? "right";
            if (activeDir == null) activeDir = d;
            else if (activeDir !== d) activeDir = null;
        }
        for (const { dir, el } of windDirButtons) {
            el.classList.toggle("green", activeDir != null && dir === activeDir);
        }
        windDirUiSyncing = false;
        syncWindStrengthUiFromSelection();
    }

    const textSide = document.createElement("aside");
    textSide.className = "adminTextSide hidden";
    textSide.setAttribute("aria-label", "Text");
    const textPanelTitle = document.createElement("div");
    textPanelTitle.className = "adminTextPanelTitle";
    textPanelTitle.textContent = "Text";
    const textContentInput = document.createElement("textarea");
    textContentInput.className = "formInput adminTextContentInput";
    textContentInput.rows = 3;
    textContentInput.maxLength = 500;
    textContentInput.placeholder = "Type here…";
    const textColorRow = document.createElement("div");
    textColorRow.className = "adminTextColorRow";
    const textColorLabel = document.createElement("span");
    textColorLabel.className = "accessHint adminTextColorLabel";
    textColorLabel.textContent = "Color";
    const textColorInput = document.createElement("input");
    textColorInput.type = "color";
    textColorInput.className = "adminTextColorInput";
    textColorInput.value = "#ffffff";
    textColorRow.appendChild(textColorLabel);
    textColorRow.appendChild(textColorInput);
    const textRotateBtn = mkBtn("Rotate", () => {
        if (!hasRotatableSelection()) return;
        selectionRotateMode = !selectionRotateMode;
        textRotateBtn.classList.toggle("green", selectionRotateMode);
        objectRotateBtn.classList.toggle("green", selectionRotateMode);
        paint();
    });
    textRotateBtn.classList.add("adminTextRotateBtn");
    let textContentUiSyncing = false;
    let textColorUiSyncing = false;
    let selectionRotateMode = false;
    type SelRotateTarget =
        | { kind: "txt"; i: number }
        | { kind: "goal" }
        | { kind: "ck"; i: number }
        | { kind: "pt"; i: number };
    let selectionRotateDrag: SelRotateTarget | null = null;

    function hasRotatableSelection(): boolean {
        return getRotatableTargets().length > 0;
    }

    function getRotatableTargets(): SelRotateTarget[] {
        const out: SelRotateTarget[] = [];
        if (selectedKeys.has("goal")) out.push({ kind: "goal" });
        for (const k of selectedKeys) {
            if (k.startsWith("txt:")) {
                const i = Number(k.slice(4));
                if (Number.isFinite(i) && def.texts?.[i]) out.push({ kind: "txt", i });
            } else if (k.startsWith("ck:")) {
                const i = Number(k.slice(3));
                if (Number.isFinite(i) && def.checkpoints?.[i]) out.push({ kind: "ck", i });
            } else if (k.startsWith("pt:")) {
                const i = Number(k.slice(3));
                if (Number.isFinite(i) && def.points?.[i]) out.push({ kind: "pt", i });
            }
        }
        return out;
    }

    function readTargetRotationDeg(target: SelRotateTarget): number {
        if (target.kind === "goal") return def.goal.rotationDeg ?? 0;
        if (target.kind === "txt") return def.texts![target.i].rotationDeg ?? 0;
        if (target.kind === "ck") return def.checkpoints![target.i].rotationDeg ?? 0;
        return def.points![target.i].rotationDeg ?? 0;
    }

    function setTargetRotationDeg(target: SelRotateTarget, deg: number) {
        if (target.kind === "goal") def.goal.rotationDeg = deg;
        else if (target.kind === "txt") def.texts![target.i].rotationDeg = deg;
        else if (target.kind === "ck") def.checkpoints![target.i].rotationDeg = deg;
        else def.points![target.i].rotationDeg = deg;
    }

    function rotateTargetHandlePx(target: SelRotateTarget, rw: number, rh: number, tAnim: number) {
        if (target.kind === "txt") return speedrunTextRotateHandlePx(def.texts![target.i], rw, rh);
        if (target.kind === "goal") {
            const g = def.goal;
            const m = Math.min(rw, rh);
            const gh = g.nHalf * m;
            const pos = speedrunPointGoalPosNorm(g.nx, g.ny, g.pathMove, g.move, tAnim);
            const cx = pos.nx * rw;
            const cy = pos.ny * rh;
            return speedrunRotateWheelHandlePx(cx, cy, gh * 1.65 + 18, g.rotationDeg ?? 0);
        }
        if (target.kind === "ck") {
            const c = def.checkpoints![target.i];
            const pos = speedrunPointGoalPosNorm(c.nx, c.ny, c.pathMove, c.move, tAnim);
            const cx = pos.nx * rw;
            const cy = pos.ny * rh;
            const ckR = speedrunCheckpointRadiusPx(c.sizeMul, 1);
            return speedrunRotateWheelHandlePx(cx, cy, ckR + 16, c.rotationDeg ?? 0);
        }
        const pt = def.points![target.i];
        const pos = speedrunPointGoalPosNorm(pt.nx, pt.ny, pt.pathMove, pt.move, tAnim);
        const cx = pos.nx * rw;
        const cy = pos.ny * rh;
        const rPt = Math.max(5, Math.min(22, 10 * (pt.radiusMul ?? 1)));
        return speedrunRotateWheelHandlePx(cx, cy, rPt + 18, pt.rotationDeg ?? 0);
    }

    function drawRotateWheelUi(
        ctx: CanvasRenderingContext2D,
        h: { cx: number; cy: number; wheelR: number; hx: number; hy: number },
    ) {
        ctx.strokeStyle = "rgba(200,220,255,0.75)";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(h.cx, h.cy, h.wheelR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = "#66ffff";
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(h.hx, h.hy, 9, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
    }

    function getSelectedTextIndices(): number[] {
        const out: number[] = [];
        for (const k of selectedKeys) {
            if (!k.startsWith("txt:")) continue;
            const i = Number(k.slice(4));
            if (Number.isFinite(i) && def.texts?.[i]) out.push(i);
        }
        return out;
    }

    function applyTextContentFromInput() {
        if (textContentUiSyncing) return;
        const val = textContentInput.value;
        for (const i of getSelectedTextIndices()) {
            const t = def.texts![i];
            t.content = val;
        }
        paint();
        maybeRecordHistory();
    }

    function applyTextColorFromInput() {
        if (textColorUiSyncing) return;
        const col = textColorInput.value.toLowerCase();
        for (const i of getSelectedTextIndices()) {
            def.texts![i].color = col;
        }
        paint();
        maybeRecordHistory();
    }

    function syncTextSidePanelFromSelection() {
        const textKeys = getSelectedTextIndices();
        const show = activeTool === "select" && textKeys.length > 0;
        textSide.classList.toggle("hidden", !show);
        if (!show) {
            if (!hasRotatableSelection()) {
                selectionRotateMode = false;
                textRotateBtn.classList.remove("green");
                objectRotateBtn.classList.remove("green");
            }
            return;
        }
        textContentUiSyncing = true;
        textColorUiSyncing = true;
        const first = def.texts![textKeys[0]];
        let mixedContent = false;
        let mixedColor = false;
        for (const i of textKeys) {
            const t = def.texts![i];
            if ((t.content ?? "") !== (first.content ?? "")) mixedContent = true;
            if ((t.color ?? "#ffffff") !== (first.color ?? "#ffffff")) mixedColor = true;
        }
        textContentInput.value = mixedContent ? "" : (first.content ?? "");
        textContentInput.placeholder = mixedContent ? "— (mixed)" : "Type here…";
        if (!mixedColor) textColorInput.value = first.color && /^#[0-9a-f]{6}$/.test(first.color) ? first.color : "#ffffff";
        textContentUiSyncing = false;
        textColorUiSyncing = false;
    }

    textContentInput.addEventListener("input", applyTextContentFromInput);
    textColorInput.addEventListener("input", applyTextColorFromInput);

    textSide.appendChild(textPanelTitle);
    textSide.appendChild(textContentInput);
    textSide.appendChild(textColorRow);
    textSide.appendChild(textRotateBtn);

    toolbar.appendChild(row2);
    toolbar.appendChild(row1);
    toolbar.appendChild(rowPointsOpts);
    toolbar.appendChild(rowPortalOpts);
    toolbar.appendChild(rowSelectLaserOpts);
    toolbar.appendChild(rowLaserDrawWidth);
    toolbar.appendChild(rowEntitySize);
    toolbar.appendChild(rowObjectRotate);
    toolbar.appendChild(rowCheck);
    toolbar.appendChild(rowUndo);
    toolbar.appendChild(rowDeleteBulk);
    toolbar.appendChild(rowAnim);
    toolbar.appendChild(windSide);
    toolbar.appendChild(textSide);
    root.appendChild(toolbar);
    root.appendChild(canvasWrap);
    mount.appendChild(root);

    let def: PenaltyLevelDef = normalizePenaltyLevelData({
        spawn: { nx: 0.5, ny: 0.9 },
        goal: { nx: 0.5, ny: 0.12, nHalf: 0.04 },
        staticSegs: [],
        moving: [],
    });

    let selectedSeg = -1;
    let pendingMovePick: WallSegDef | null = null;

    type MovePathDraft = {
        groupRest: { nx: number; ny: number };
        anchors: { nx: number; ny: number }[];
        targetKeys: SelKey[];
        /** If true, the path draft is cleared when multi-selection changes on pointer up. */
        fromSelection: boolean;
    };
    let movePathDraft: MovePathDraft | null = null;

    function clearMovePathDraft() {
        movePathDraft = null;
    }

    function syncPathDraftUi() {
        const n = movePathDraft?.anchors.length ?? 0;
        const editingSpeedOnly =
            activeTool === "select" &&
            getMoveSpeedEditTarget() !== null &&
            !(moveWallsEnabled && movePathDraft);
        if (editingSpeedOnly && !moveWallsEnabled) {
            pathDoneBtn.classList.add("hidden");
            pathLoopRow.classList.add("hidden");
            return;
        }
        pathDoneBtn.classList.toggle("hidden", n < 1);
        pathLoopRow.classList.toggle("hidden", n < 2);
        if (n < 2) pathLoopCheck.checked = false;
    }
    let dragLineStart: { nx: number; ny: number } | null = null;
    let squareStart: { nx: number; ny: number } | null = null;
    let windSquareStart: { nx: number; ny: number } | null = null;
    let textSquareStart: { nx: number; ny: number } | null = null;
    let circleStart: { nx: number; ny: number } | null = null;
    let portalDragStart: { nx: number; ny: number } | null = null;
    let paintLast: { nx: number; ny: number } | null = null;

    let lastPtrNorm: { nx: number; ny: number } | null = null;

    type SelKey = string;
    const selectedKeys = new Set<SelKey>();
    let entityDrag: null | "spawn" | "ballSpawn" | "goal" = null;
    type BulkSnap = {
        segs: Map<number, { ax: number; ay: number; bx: number; by: number }>;
        pts: Map<number, { nx: number; ny: number; move?: { tnx: number; tny: number } }>;
        mvs: Map<number, { y: number; cx: number; move?: { ty: number; tcx: number } }>;
        winds?: Map<number, { nx: number; ny: number; nw: number; nh: number }>;
        texts?: Map<number, { nx: number; ny: number; nw: number; nh: number }>;
        spawn?: { nx: number; ny: number };
        goal?: { nx: number; ny: number; move?: { tnx: number; tny: number } };
        cks?: Map<number, { nx: number; ny: number; move?: { tnx: number; tny: number } }>;
        tspawn?: { nx: number; ny: number; move?: { tnx: number; tny: number } };
        portals?: Map<number, { ax: number; ay: number; bx: number; by: number }>;
    };
    let bulkDrag: null | { startNx: number; startNy: number; snap: BulkSnap } = null;
    let selectMarqueeAnchor: { nx: number; ny: number } | null = null;
    let selectMarqueePick: SelKey | null = null;
    let selectPointerMode: null | "move" | "box" = null;
    let entitySizeUiSyncing = false;
    let sizeHistTimer: number | null = null;

    function clearSelectionState() {
        selectedKeys.clear();
        entityDrag = null;
        bulkDrag = null;
        selectMarqueeAnchor = null;
        selectMarqueePick = null;
        selectPointerMode = null;
    }

    function getMoveSpeedEditTarget():
        | { kind: "path"; seg: WallSegDef }
        | { kind: "osc"; seg: WallSegDef }
        | null {
        if (activeTool !== "select") return null;
        if (selectedKeys.size !== 1) return null;
        const k = [...selectedKeys][0];
        if (!k.startsWith("seg:")) return null;
        const i = Number(k.slice(4));
        const seg = def.staticSegs[i];
        if (!seg) return null;
        if (seg.pathMove) return { kind: "path", seg };
        if (seg.move && seg.move.outS > 0 && seg.move.backS > 0) return { kind: "osc", seg };
        return null;
    }

    let moveSpeedUiSyncing = false;
    let moveSpeedHistTimer: number | null = null;
    function scheduleMoveSpeedHistory() {
        if (moveSpeedHistTimer != null) window.clearTimeout(moveSpeedHistTimer);
        moveSpeedHistTimer = window.setTimeout(() => {
            moveSpeedHistTimer = null;
            maybeRecordHistory();
        }, 350);
    }

    function applyMoveSpeedEditFromInputs(): boolean {
        const t = getMoveSpeedEditTarget();
        if (!t) return false;
        const outS = clamp(Number(admMoveOut.value || 1.2), 0.05, 120);
        const backS = clamp(Number(admMoveBack.value || 1.2), 0.05, 120);
        if (t.kind === "path") {
            const pm = t.seg.pathMove!;
            if (Math.abs(pm.legOutS - outS) < 1e-8 && Math.abs(pm.legBackS - backS) < 1e-8) return false;
            pm.legOutS = outS;
            pm.legBackS = backS;
            return true;
        }
        const m = t.seg.move!;
        if (Math.abs(m.outS - outS) < 1e-8 && Math.abs(m.backS - backS) < 1e-8) return false;
        m.outS = outS;
        m.backS = backS;
        return true;
    }

    function syncMoveSpeedUiFromSelection() {
        const t = getMoveSpeedEditTarget();
        const draftBusy = moveWallsEnabled && movePathDraft != null;
        if (!t || draftBusy) return;
        moveSpeedUiSyncing = true;
        if (t.kind === "path") {
            const pm = t.seg.pathMove!;
            admMoveOut.value = String(pm.legOutS);
            admMoveBack.value = String(pm.legBackS);
        } else {
            const m = t.seg.move!;
            admMoveOut.value = String(m.outS);
            admMoveBack.value = String(m.backS);
        }
        moveSpeedUiSyncing = false;
    }

    const ctx = canvas.getContext("2d")!;

    const MAX_HIST = 60;
    let history: PenaltyLevelDef[] = [];
    let histIdx = 0;

    function defJsonSig(d: PenaltyLevelDef) {
        return JSON.stringify(levelToJson(d));
    }

    function syncUndoRedo() {
        undoBtn.disabled = histIdx <= 0;
        redoBtn.disabled = histIdx >= history.length - 1;
    }

    function applyDefFromHistory(d: PenaltyLevelDef) {
        def = deepCloneDef(d);
        selectedSeg = -1;
        pendingMovePick = null;
        movePathDraft = null;
        syncPathDraftUi();
        dragLineStart = null;
        squareStart = null;
        windSquareStart = null;
        textSquareStart = null;
        circleStart = null;
        paintLast = null;
        clearSelectionState();
        selectionRotateMode = false;
        textRotateBtn.classList.remove("green");
        objectRotateBtn.classList.remove("green");
        paint();
        syncUndoRedo();
    }

    function resetHistoryFromDef() {
        history = [deepCloneDef(def)];
        histIdx = 0;
        syncUndoRedo();
    }

    function maybeRecordHistory() {
        const cur = deepCloneDef(def);
        if (histIdx >= 0 && defJsonSig(cur) === defJsonSig(history[histIdx])) return;
        history.splice(histIdx + 1);
        history.push(cur);
        histIdx = history.length - 1;
        while (history.length > MAX_HIST) {
            history.shift();
            histIdx--;
        }
        syncUndoRedo();
    }

    undoBtn.onclick = () => {
        if (histIdx <= 0) return;
        histIdx--;
        applyDefFromHistory(history[histIdx]);
    };
    redoBtn.onclick = () => {
        if (histIdx >= history.length - 1) return;
        histIdx++;
        applyDefFromHistory(history[histIdx]);
    };

    rowUndo.appendChild(
        mkBtn("Reset", () => {
            if (
                !confirm(
                    "Reset everything: lasers and points are removed, checkpoint cleared, spawn and goal return to defaults, and all motion / rotation is removed.",
                )
            )
                return;
            def = normalizePenaltyLevelData({
                spawn: { nx: 0.5, ny: 0.9 },
                goal: { nx: 0.5, ny: 0.12, nHalf: 0.04 },
                staticSegs: [],
                moving: [],
            });
            selectedSeg = -1;
            pendingMovePick = null;
            moveWallsEnabled = false;
            rotateWallsEnabled = false;
            moveWallCheck.checked = false;
            rotWallCheck.checked = false;
            clearMovePathDraft();
            syncPathDraftUi();
            syncAnimPanelVisibility();
            dragLineStart = null;
            squareStart = null;
            windSquareStart = null;
            circleStart = null;
            portalDragStart = null;
            paintLast = null;
            clearSelectionState();
            resetHistoryFromDef();
            paint();
        }),
    );

    type EditorClipboardItem =
        | { kind: "seg"; data: WallSegDef }
        | { kind: "mv"; data: MovingLaserDef }
        | { kind: "pt"; data: import("./penalty-levels").SpeedrunPointDef }
        | { kind: "wind"; data: import("./penalty-levels").SpeedrunWindDef }
        | { kind: "txt"; data: import("./penalty-levels").SpeedrunTextDef }
        | { kind: "ck"; data: import("./penalty-levels").SpeedrunCheckpointDef }
        | { kind: "tspawn"; data: NonNullable<PenaltyLevelDef["temporalSpawn"]> }
        | { kind: "portal"; data: SpeedrunPortalDef };

    let editorClipboard: EditorClipboardItem[] | null = null;
    const PASTE_OFFSET = 0.025;

    function cloneJson<T>(v: T): T {
        return JSON.parse(JSON.stringify(v)) as T;
    }

    function copySelection() {
        const keys = [...selectedKeys].filter((k) => k !== "spawn" && k !== "goal");
        if (!keys.length) return;
        const items: EditorClipboardItem[] = [];
        for (const k of keys) {
            const cki = parseCkKey(k);
            if (cki != null) {
                const ck = def.checkpoints?.[cki];
                if (ck) items.push({ kind: "ck", data: cloneJson(ck) });
            } else if (k === "tspawn" && def.temporalSpawn) {
                items.push({ kind: "tspawn", data: cloneJson(def.temporalSpawn) });
            } else if (k.startsWith("seg:")) {
                const i = Number(k.slice(4));
                const s = def.staticSegs[i];
                if (s) items.push({ kind: "seg", data: cloneJson(s) });
            } else if (k.startsWith("mv:")) {
                const i = Number(k.slice(3));
                const m = def.moving[i];
                if (m) items.push({ kind: "mv", data: cloneJson(m) });
            } else if (k.startsWith("pt:")) {
                const i = Number(k.slice(3));
                const p = def.points?.[i];
                if (p) items.push({ kind: "pt", data: cloneJson(p) });
            } else if (k.startsWith("wind:")) {
                const i = Number(k.slice(5));
                const w = def.winds?.[i];
                if (w) items.push({ kind: "wind", data: cloneJson({ ...w, id: uid() }) });
            } else if (k.startsWith("txt:")) {
                const i = Number(k.slice(4));
                const t = def.texts?.[i];
                if (t) items.push({ kind: "txt", data: cloneJson({ ...t, id: uid() }) });
            } else if (k.startsWith("portal:")) {
                const i = Number(k.slice(7));
                const p = def.portals?.[i];
                if (p) items.push({ kind: "portal", data: cloneJson({ ...p, id: uid() }) });
            }
        }
        if (!items.length) return;
        editorClipboard = items;
    }

    function offsetNormSeg(seg: WallSegDef, dx: number, dy: number) {
        seg.ax = clamp(seg.ax + dx, 0, 1);
        seg.ay = clamp(seg.ay + dy, 0, 1);
        seg.bx = clamp(seg.bx + dx, 0, 1);
        seg.by = clamp(seg.by + dy, 0, 1);
        if (seg.move) {
            seg.move.tax = clamp(seg.move.tax + dx, 0, 1);
            seg.move.tay = clamp(seg.move.tay + dy, 0, 1);
            seg.move.tbx = clamp(seg.move.tbx + dx, 0, 1);
            seg.move.tby = clamp(seg.move.tby + dy, 0, 1);
        }
        if (seg.pathMove) {
            seg.pathMove.restNx = clamp(seg.pathMove.restNx + dx, 0, 1);
            seg.pathMove.restNy = clamp(seg.pathMove.restNy + dy, 0, 1);
            for (const a of seg.pathMove.anchors) {
                a.nx = clamp(a.nx + dx, 0, 1);
                a.ny = clamp(a.ny + dy, 0, 1);
            }
        }
        seg.id = uid();
    }

    function offsetPointLike(
        obj: { nx: number; ny: number; move?: { tnx: number; tny: number }; pathMove?: OscPathMove },
        dx: number,
        dy: number,
    ) {
        obj.nx = clamp(obj.nx + dx, 0, 1);
        obj.ny = clamp(obj.ny + dy, 0, 1);
        if (obj.move) {
            obj.move.tnx = clamp(obj.move.tnx + dx, 0, 1);
            obj.move.tny = clamp(obj.move.tny + dy, 0, 1);
        }
        if (obj.pathMove) {
            obj.pathMove.restNx = clamp(obj.pathMove.restNx + dx, 0, 1);
            obj.pathMove.restNy = clamp(obj.pathMove.restNy + dy, 0, 1);
            for (const a of obj.pathMove.anchors) {
                a.nx = clamp(a.nx + dx, 0, 1);
                a.ny = clamp(a.ny + dy, 0, 1);
            }
        }
    }

    function pasteClipboard() {
        if (!editorClipboard?.length) return;
        clearSelectionState();
        const dx = PASTE_OFFSET;
        const dy = PASTE_OFFSET;
        for (const item of editorClipboard) {
            if (item.kind === "seg") {
                const s = cloneJson(item.data);
                offsetNormSeg(s, dx, dy);
                def.staticSegs.push(s);
                selectedKeys.add(`seg:${def.staticSegs.length - 1}`);
            } else if (item.kind === "mv") {
                const m = cloneJson(item.data);
                m.y = clamp(m.y + dy, 0, 1);
                if (m.cx != null) m.cx = clamp(m.cx + dx, 0, 1);
                if (m.move) {
                    m.move.ty = clamp(m.move.ty + dy, 0, 1);
                    m.move.tcx = clamp(m.move.tcx + dx, 0, 1);
                }
                if (m.pathMove) {
                    m.pathMove.restNx = clamp(m.pathMove.restNx + dx, 0, 1);
                    m.pathMove.restNy = clamp(m.pathMove.restNy + dy, 0, 1);
                    for (const a of m.pathMove.anchors) {
                        a.nx = clamp(a.nx + dx, 0, 1);
                        a.ny = clamp(a.ny + dy, 0, 1);
                    }
                }
                def.moving.push(m);
                selectedKeys.add(`mv:${def.moving.length - 1}`);
            } else if (item.kind === "pt") {
                if (!def.points) def.points = [];
                const p = cloneJson(item.data);
                offsetPointLike(p, dx, dy);
                def.points.push(p);
                selectedKeys.add(`pt:${def.points.length - 1}`);
            } else if (item.kind === "wind") {
                if (!def.winds) def.winds = [];
                const w = cloneJson(item.data);
                w.nx = clamp(w.nx + dx, 0, 1);
                w.ny = clamp(w.ny + dy, 0, 1);
                def.winds.push(w);
                selectedKeys.add(`wind:${def.winds.length - 1}`);
            } else if (item.kind === "txt") {
                if (!def.texts) def.texts = [];
                const t = cloneJson(item.data);
                t.nx = clamp(t.nx + dx, 0, 1);
                t.ny = clamp(t.ny + dy, 0, 1);
                def.texts.push(t);
                selectedKeys.add(`txt:${def.texts.length - 1}`);
            } else if (item.kind === "ck") {
                const c = cloneJson(item.data);
                offsetPointLike(c, dx, dy);
                const cks = ensureCheckpoints(def);
                cks.push(c);
                selectedKeys.add(ckKey(cks.length - 1));
            } else if (item.kind === "tspawn") {
                const c = cloneJson(item.data);
                offsetPointLike(c, dx, dy);
                def.temporalSpawn = c;
                selectedKeys.add("tspawn");
            } else if (item.kind === "portal") {
                const p = cloneJson(item.data);
                p.ax = clamp(p.ax + dx, 0, 1);
                p.ay = clamp(p.ay + dy, 0, 1);
                p.bx = clamp(p.bx + dx, 0, 1);
                p.by = clamp(p.by + dy, 0, 1);
                p.id = uid();
                const idx = upsertPortalLine(p.group, p.side, p.ax, p.ay, p.bx, p.by);
                selectedKeys.add(portalKey(idx));
            }
        }
        paint();
        maybeRecordHistory();
    }

    document.addEventListener("keydown", onEditorKeydown);
    function onEditorKeydown(ev: KeyboardEvent) {
        const t = ev.target as HTMLElement | null;
        if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable))
            return;
        const mod = ev.ctrlKey || ev.metaKey;
        if (mod && (ev.key === "z" || ev.key === "Z")) {
            ev.preventDefault();
            if (ev.shiftKey) {
                if (histIdx < history.length - 1) {
                    histIdx++;
                    applyDefFromHistory(history[histIdx]);
                }
            } else if (histIdx > 0) {
                histIdx--;
                applyDefFromHistory(history[histIdx]);
            }
            return;
        }
        if (mod && (ev.key === "y" || ev.key === "Y")) {
            ev.preventDefault();
            if (histIdx < history.length - 1) {
                histIdx++;
                applyDefFromHistory(history[histIdx]);
            }
            return;
        }
        if (!mod && (ev.key === "r" || ev.key === "R") && activeTool === "select" && hasRotatableSelection()) {
            ev.preventDefault();
            selectionRotateMode = !selectionRotateMode;
            textRotateBtn.classList.toggle("green", selectionRotateMode);
            objectRotateBtn.classList.toggle("green", selectionRotateMode);
            paint();
            return;
        }
        if (!mod && ev.key === "Delete") {
            ev.preventDefault();
            deleteSelectedDeletableObjects();
            return;
        }
        if (!mod) return;
        if (ev.key === "s" || ev.key === "S") {
            ev.preventDefault();
            applyToolChoice("select");
            return;
        }
        if (ev.key === "t" || ev.key === "T") {
            ev.preventDefault();
            runPlayTest();
            return;
        }
        if (ev.key === "c" || ev.key === "C") {
            ev.preventDefault();
            copySelection();
        } else if (ev.key === "v" || ev.key === "V") {
            ev.preventDefault();
            pasteClipboard();
        }
    }

    type EntitySizeKind =
        | { kind: "goal" }
        | { kind: "pt"; keys: SelKey[] }
        | { kind: "txt"; keys: SelKey[] }
        | { kind: "ck"; keys: SelKey[] }
        | { kind: "seg" | "mv"; keys: SelKey[] };

    function getEntitySizeKind(): EntitySizeKind | null {
        const keys = [...selectedKeys].filter((k) => k !== "spawn" && k !== "tspawn");
        if (keys.length === 0) return null;
        if (keys.length === 1 && keys[0] === "goal") return { kind: "goal" };
        const pref = (k: SelKey) => (k.includes(":") ? k.slice(0, k.indexOf(":")) : k);
        const p0 = pref(keys[0]);
        if (p0 === "pt" && keys.every((k) => pref(k) === "pt")) return { kind: "pt", keys };
        if (p0 === "txt" && keys.every((k) => pref(k) === "txt")) return { kind: "txt", keys };
        if (p0 === "ck" && keys.every((k) => pref(k) === "ck")) return { kind: "ck", keys };
        if (p0 === "seg" && keys.every((k) => pref(k) === "seg")) return { kind: "seg", keys };
        if (p0 === "mv" && keys.every((k) => pref(k) === "mv")) return { kind: "mv", keys };
        return null;
    }

    function readTextSizeMetric(t: import("./penalty-levels").SpeedrunTextDef): number {
        return Math.sqrt(Math.max(1e-8, t.nw * t.nh));
    }

    function applyTextSizeMetric(t: import("./penalty-levels").SpeedrunTextDef, metric: number) {
        const cur = readTextSizeMetric(t);
        if (cur < 1e-8) return;
        const scale = metric / cur;
        const cx = t.nx + t.nw / 2;
        const cy = t.ny + t.nh / 2;
        t.nw = clamp(t.nw * scale, 0.02, 0.55);
        t.nh = clamp(t.nh * scale, 0.02, 0.55);
        t.nx = clamp(cx - t.nw / 2, 0, 1 - t.nw);
        t.ny = clamp(cy - t.nh / 2, 0, 1 - t.nh);
    }

    function readEntitySizeValue(info: EntitySizeKind): number {
        if (info.kind === "goal") return def.goal.nHalf;
        const k0 = info.keys[0];
        if (info.kind === "seg") {
            const i = Number(k0.slice(4));
            return def.staticSegs[i]?.lineHalfMul ?? 1;
        }
        if (info.kind === "mv") {
            const i = Number(k0.slice(3));
            return def.moving[i]?.lineHalfMul ?? 1;
        }
        if (info.kind === "txt") {
            const i = Number(k0.slice(4));
            const t = def.texts?.[i];
            return t ? readTextSizeMetric(t) : 0.08;
        }
        if (info.kind === "ck") {
            const i = Number(k0.slice(3));
            return def.checkpoints?.[i]?.sizeMul ?? 1;
        }
        const i = Number(k0.slice(3));
        return def.points?.[i]?.radiusMul ?? 1;
    }

    function applyEntitySizeValue(info: EntitySizeKind, v: number) {
        if (info.kind === "goal") {
            def.goal.nHalf = clamp(v, 0.015, 0.14);
            return;
        }
        if (info.kind === "txt") {
            const metric = clamp(v, 0.025, 0.42);
            for (const k of info.keys) {
                const i = Number(k.slice(4));
                const t = def.texts?.[i];
                if (!t) continue;
                applyTextSizeMetric(t, metric);
            }
            return;
        }
        if (info.kind === "ck") {
            const sm = clamp(v, 0.45, 2.5);
            for (const k of info.keys) {
                const i = Number(k.slice(3));
                const c = def.checkpoints?.[i];
                if (!c) continue;
                if (Math.abs(sm - 1) < 0.0001) delete c.sizeMul;
                else c.sizeMul = sm;
            }
            return;
        }
        if (info.kind === "seg") {
            const m = clamp(v, 0.25, 5);
            for (const k of info.keys) {
                const i = Number(k.slice(4));
                const s = def.staticSegs[i];
                if (!s) continue;
                if (Math.abs(m - 1) < 0.0001) delete s.lineHalfMul;
                else s.lineHalfMul = m;
            }
            return;
        }
        if (info.kind === "mv") {
            const m = clamp(v, 0.25, 5);
            for (const k of info.keys) {
                const i = Number(k.slice(3));
                const mv = def.moving[i];
                if (!mv) continue;
                if (Math.abs(m - 1) < 0.0001) delete mv.lineHalfMul;
                else mv.lineHalfMul = m;
            }
            return;
        }
        const rm = clamp(v, 0.35, 3);
        for (const k of info.keys) {
            const i = Number(k.slice(3));
            const p = def.points?.[i];
            if (!p) continue;
            if (Math.abs(rm - 1) < 0.0001) delete p.radiusMul;
            else p.radiusMul = rm;
        }
    }

    function scheduleEntitySizeHistory() {
        if (sizeHistTimer != null) window.clearTimeout(sizeHistTimer);
        sizeHistTimer = window.setTimeout(() => {
            sizeHistTimer = null;
            maybeRecordHistory();
        }, 350);
    }

    function configureEntitySizeControls(info: EntitySizeKind) {
        if (info.kind === "goal") {
            entitySizeSlider.min = "0.015";
            entitySizeSlider.max = "0.14";
            entitySizeSlider.step = "0.002";
            entitySizeNum.min = "0.015";
            entitySizeNum.max = "0.14";
            entitySizeNum.step = "0.002";
            entitySizeLabel.textContent = "Goal size";
            return;
        }
        if (info.kind === "txt") {
            entitySizeSlider.min = "0.025";
            entitySizeSlider.max = "0.42";
            entitySizeSlider.step = "0.005";
            entitySizeNum.min = "0.025";
            entitySizeNum.max = "0.42";
            entitySizeNum.step = "0.005";
            entitySizeLabel.textContent = "Text size";
            return;
        }
        if (info.kind === "ck") {
            entitySizeSlider.min = "0.45";
            entitySizeSlider.max = "2.5";
            entitySizeSlider.step = "0.05";
            entitySizeNum.min = "0.45";
            entitySizeNum.max = "2.5";
            entitySizeNum.step = "0.05";
            entitySizeLabel.textContent = "Checkpoint size";
            return;
        }
        if (info.kind === "seg" || info.kind === "mv") {
            entitySizeSlider.min = "0.25";
            entitySizeSlider.max = "5";
            entitySizeSlider.step = "0.05";
            entitySizeNum.min = "0.25";
            entitySizeNum.max = "5";
            entitySizeNum.step = "0.05";
            entitySizeLabel.textContent = info.kind === "seg" ? "Laser width" : "Moving width";
            return;
        }
        entitySizeSlider.min = "0.35";
        entitySizeSlider.max = "3";
        entitySizeSlider.step = "0.05";
        entitySizeNum.min = "0.35";
        entitySizeNum.max = "3";
        entitySizeNum.step = "0.05";
        entitySizeLabel.textContent = "Point size";
    }

    function syncObjectRotateRow() {
        const show =
            activeTool === "select" &&
            !moveWallsEnabled &&
            !rotateWallsEnabled &&
            hasRotatableSelection();
        rowObjectRotate.classList.toggle("hidden", !show);
    }

    function syncEntitySizeRow() {
        const info = getEntitySizeKind();
        const show =
            activeTool === "select" &&
            !moveWallsEnabled &&
            !rotateWallsEnabled &&
            info != null;
        rowEntitySize.classList.toggle("hidden", !show);
        syncObjectRotateRow();
        if (!info) return;
        configureEntitySizeControls(info);
        const v = readEntitySizeValue(info);
        entitySizeUiSyncing = true;
        entitySizeSlider.value = String(v);
        entitySizeNum.value =
            info.kind === "goal"
                ? v.toFixed(3)
                : info.kind === "txt"
                  ? v.toFixed(3)
                  : Number.isInteger(v)
                    ? String(v)
                    : v.toFixed(2);
        entitySizeUiSyncing = false;
    }

    entitySizeSlider.addEventListener("input", () => {
        if (entitySizeUiSyncing) return;
        const info = getEntitySizeKind();
        if (!info) return;
        let v = Number(entitySizeSlider.value);
        if (info.kind === "goal") v = clamp(v, 0.015, 0.14);
        else if (info.kind === "pt") v = clamp(v, 0.35, 3);
        else if (info.kind === "txt") v = clamp(v, 0.025, 0.42);
        else if (info.kind === "ck") v = clamp(v, 0.45, 2.5);
        else v = clamp(v, 0.25, 5);
        applyEntitySizeValue(info, v);
        entitySizeUiSyncing = true;
        entitySizeNum.value =
            info.kind === "goal"
                ? v.toFixed(3)
                : info.kind === "txt"
                  ? v.toFixed(3)
                  : Number.isInteger(v)
                    ? String(v)
                    : v.toFixed(2);
        entitySizeUiSyncing = false;
        paint();
        scheduleEntitySizeHistory();
    });

    entitySizeNum.addEventListener("input", () => {
        if (entitySizeUiSyncing) return;
        const info = getEntitySizeKind();
        if (!info) return;
        let v = Number(entitySizeNum.value);
        if (!Number.isFinite(v)) return;
        if (info.kind === "goal") v = clamp(v, 0.015, 0.14);
        else if (info.kind === "pt") v = clamp(v, 0.35, 3);
        else if (info.kind === "txt") v = clamp(v, 0.025, 0.42);
        else if (info.kind === "ck") v = clamp(v, 0.45, 2.5);
        else v = clamp(v, 0.25, 5);
        applyEntitySizeValue(info, v);
        entitySizeUiSyncing = true;
        entitySizeSlider.value = String(v);
        entitySizeUiSyncing = false;
        paint();
        scheduleEntitySizeHistory();
    });

    entitySizeNum.addEventListener("change", () => {
        if (entitySizeUiSyncing) return;
        const info = getEntitySizeKind();
        if (!info) return;
        let v = Number(entitySizeNum.value);
        if (!Number.isFinite(v)) {
            syncEntitySizeRow();
            return;
        }
        if (info.kind === "goal") v = clamp(v, 0.015, 0.14);
        else if (info.kind === "pt") v = clamp(v, 0.35, 3);
        else if (info.kind === "txt") v = clamp(v, 0.025, 0.42);
        else if (info.kind === "ck") v = clamp(v, 0.45, 2.5);
        else v = clamp(v, 0.25, 5);
        applyEntitySizeValue(info, v);
        entitySizeUiSyncing = true;
        entitySizeSlider.value = String(v);
        entitySizeNum.value =
            info.kind === "goal"
                ? v.toFixed(3)
                : info.kind === "txt"
                  ? v.toFixed(3)
                  : Number.isInteger(v)
                    ? String(v)
                    : v.toFixed(2);
        entitySizeUiSyncing = false;
        paint();
        maybeRecordHistory();
    });

    function hitSeg(nx: number, ny: number): number {
        let best = -1;
        let bestD = 0.028;
        for (let i = 0; i < def.staticSegs.length; i++) {
            const d = distPointSegNorm(nx, ny, def.staticSegs[i]);
            if (d < bestD) {
                bestD = d;
                best = i;
            }
        }
        return best;
    }

    function laserSegNorm(d: MovingLaserDef, tSec: number) {
        const s = movingLaserSegNorm(d, tSec);
        return { x1: s.ax, y1: s.ay, x2: s.bx, y2: s.by };
    }

    function distLaserNorm(nx: number, ny: number, d: MovingLaserDef, tSec: number) {
        const s = laserSegNorm(d, tSec);
        return distPointToSeg(nx, ny, s.x1, s.y1, s.x2, s.y2);
    }

    function hitMovingLaser(nx: number, ny: number, tSec: number): number {
        let best = -1;
        let bestD = 0.035;
        for (let i = 0; i < def.moving.length; i++) {
            const d = distLaserNorm(nx, ny, def.moving[i], tSec);
            if (d < bestD) {
                bestD = d;
                best = i;
            }
        }
        return best;
    }

    function hitSpawnNorm(nx: number, ny: number) {
        const s = def.spawn;
        return Math.hypot(nx - s.nx, ny - s.ny) < 0.056;
    }

    function hitBallSpawnNorm(nx: number, ny: number) {
        const s = def.ballSpawn;
        return Math.hypot(nx - s.nx, ny - s.ny) < 0.056;
    }

    function hitPortalNorm(nx: number, ny: number): number {
        const arr = def.portals;
        if (!arr?.length) return -1;
        let best = -1;
        let bestD = 0.028;
        for (let i = arr.length - 1; i >= 0; i--) {
            const p = arr[i];
            const d = distPointSegNorm(nx, ny, { ax: p.ax, ay: p.ay, bx: p.bx, by: p.by });
            if (d < bestD) {
                bestD = d;
                best = i;
            }
        }
        return best;
    }

    function hitGoalNorm(nx: number, ny: number) {
        const g = def.goal;
        const tPick = pauseAnimationsCheck.checked ? 0 : performance.now() / 1000;
        const pos = speedrunPointGoalPosNorm(g.nx, g.ny, g.pathMove, g.move, tPick);
        const half = Math.max(g.nHalf * 2.2, 0.05);
        return Math.abs(nx - pos.nx) < half && Math.abs(ny - pos.ny) < half;
    }

    function hitPointNorm(nx: number, ny: number): number {
        const pts = def.points;
        if (!pts) return -1;
        const tPick = pauseAnimationsCheck.checked ? 0 : performance.now() / 1000;
        let best = -1;
        let bestD = Infinity;
        for (let i = 0; i < pts.length; i++) {
            const p = pts[i];
            const pos = speedrunPointGoalPosNorm(p.nx, p.ny, p.pathMove, p.move, tPick);
            const d = Math.hypot(nx - pos.nx, ny - pos.ny);
            const th = 0.045 * Math.min(2.5, p.radiusMul ?? 1);
            if (d < th && d < bestD) {
                bestD = d;
                best = i;
            }
        }
        return best;
    }

    function hitCheckpointIndexNorm(nx: number, ny: number): number {
        const cks = def.checkpoints ?? [];
        if (!cks.length) return -1;
        const tPick = pauseAnimationsCheck.checked ? 0 : performance.now() / 1000;
        for (let i = cks.length - 1; i >= 0; i--) {
            const c = cks[i];
            const pos = speedrunPointGoalPosNorm(c.nx, c.ny, c.pathMove, c.move, tPick);
            if (Math.abs(nx - pos.nx) < 0.04 && Math.abs(ny - pos.ny) < 0.04) return i;
        }
        return -1;
    }

    function hitTemporalSpawnNorm(nx: number, ny: number) {
        const c = def.temporalSpawn;
        if (!c) return false;
        const tPick = pauseAnimationsCheck.checked ? 0 : performance.now() / 1000;
        const pos = speedrunPointGoalPosNorm(c.nx, c.ny, c.pathMove, c.move, tPick);
        return Math.abs(nx - pos.nx) < 0.04 && Math.abs(ny - pos.ny) < 0.04;
    }

    function hitWindNorm(nx: number, ny: number): number {
        const arr = def.winds;
        if (!arr?.length) return -1;
        for (let i = arr.length - 1; i >= 0; i--) {
            if (normPointInWindZone(nx, ny, arr[i])) return i;
        }
        return -1;
    }

    function hitTextNorm(nx: number, ny: number): number {
        const arr = def.texts;
        if (!arr?.length) return -1;
        for (let i = arr.length - 1; i >= 0; i--) {
            if (normPointInTextZone(nx, ny, arr[i])) return i;
        }
        return -1;
    }

    function hitSelectionRotateHandle(nx: number, ny: number, rw: number, rh: number, tAnim: number): SelRotateTarget | null {
        if (!selectionRotateMode) return null;
        const px = nx * rw;
        const py = ny * rh;
        for (const target of getRotatableTargets()) {
            const h = rotateTargetHandlePx(target, rw, rh, tAnim);
            if (Math.hypot(px - h.hx, py - h.hy) < Math.max(14, h.wheelR * 0.08)) return target;
        }
        return null;
    }

    function normRectsIntersect(
        a: { x1: number; x2: number; y1: number; y2: number },
        b: { x1: number; x2: number; y1: number; y2: number },
    ) {
        if (a.x2 < b.x1 || a.x1 > b.x2 || a.y2 < b.y1 || a.y1 > b.y2) return false;
        return true;
    }

    /** Topmost small target first. */
    function pickSelectableAt(nx: number, ny: number, tSec: number): SelKey | null {
        const pi = hitPointNorm(nx, ny);
        if (pi >= 0) return `pt:${pi}`;
        const si = hitSeg(nx, ny);
        if (si >= 0) return `seg:${si}`;
        const mi = hitMovingLaser(nx, ny, tSec);
        if (mi >= 0) return `mv:${mi}`;
        const portalHit = hitPortalNorm(nx, ny);
        if (portalHit >= 0) return portalKey(portalHit);
        const ci = hitCheckpointIndexNorm(nx, ny);
        if (ci >= 0) return ckKey(ci);
        if (hitTemporalSpawnNorm(nx, ny)) return "tspawn";
        if (hitSpawnNorm(nx, ny)) return "spawn";
        if (hitGoalNorm(nx, ny)) return "goal";
        const ti = hitTextNorm(nx, ny);
        if (ti >= 0) return `txt:${ti}`;
        const wvi = hitWindNorm(nx, ny);
        if (wvi >= 0) return `wind:${wvi}`;
        return null;
    }

    function normRectFromCorners(nx1: number, ny1: number, nx2: number, ny2: number) {
        return {
            x1: Math.min(nx1, nx2),
            x2: Math.max(nx1, nx2),
            y1: Math.min(ny1, ny2),
            y2: Math.max(ny1, ny2),
        };
    }

    function pointInNormRect(
        nx: number,
        ny: number,
        box: { x1: number; x2: number; y1: number; y2: number },
        pad: number,
    ) {
        return nx >= box.x1 - pad && nx <= box.x2 + pad && ny >= box.y1 - pad && ny <= box.y2 + pad;
    }

    function segNormBBoxIntersectsRect(
        s: WallSegDef,
        box: { x1: number; x2: number; y1: number; y2: number },
        pad: number,
    ) {
        const sx1 = Math.min(s.ax, s.bx) - pad;
        const sx2 = Math.max(s.ax, s.bx) + pad;
        const sy1 = Math.min(s.ay, s.by) - pad;
        const sy2 = Math.max(s.ay, s.by) + pad;
        if (sx2 < box.x1 || sx1 > box.x2 || sy2 < box.y1 || sy1 > box.y2) return false;
        return true;
    }

    function applyMarqueeSelection(nx1: number, ny1: number, nx2: number, ny2: number, additive: boolean) {
        const r = normRectFromCorners(nx1, ny1, nx2, ny2);
        if (r.x2 - r.x1 < 0.006 || r.y2 - r.y1 < 0.006) return;
        const pad = 0.012;
        const next = additive ? new Set(selectedKeys) : new Set<SelKey>();
        for (let i = 0; i < def.staticSegs.length; i++) {
            if (segNormBBoxIntersectsRect(def.staticSegs[i], r, pad)) next.add(`seg:${i}`);
        }
        const pts = def.points;
        if (pts) {
            for (let i = 0; i < pts.length; i++) {
                if (pointInNormRect(pts[i].nx, pts[i].ny, r, pad)) next.add(`pt:${i}`);
            }
        }
        const tM = pauseAnimationsCheck.checked ? 0 : performance.now() / 1000;
        for (let i = 0; i < def.moving.length; i++) {
            const seg = laserSegNorm(def.moving[i], tM);
            const sx1 = Math.min(seg.x1, seg.x2) - pad;
            const sx2 = Math.max(seg.x1, seg.x2) + pad;
            const sy1 = Math.min(seg.y1, seg.y2) - pad;
            const sy2 = Math.max(seg.y1, seg.y2) + pad;
            if (!(sx2 < r.x1 || sx1 > r.x2 || sy2 < r.y1 || sy1 > r.y2)) next.add(`mv:${i}`);
        }
        const cks = def.checkpoints ?? [];
        for (let i = 0; i < cks.length; i++) {
            const tPickCk = pauseAnimationsCheck.checked ? 0 : performance.now() / 1000;
            const pos = speedrunPointGoalPosNorm(cks[i].nx, cks[i].ny, cks[i].pathMove, cks[i].move, tPickCk);
            if (pointInNormRect(pos.nx, pos.ny, r, pad)) next.add(ckKey(i));
        }
        if (def.temporalSpawn) {
            const ts = def.temporalSpawn;
            const tPickTs = pauseAnimationsCheck.checked ? 0 : performance.now() / 1000;
            const tpos = speedrunPointGoalPosNorm(ts.nx, ts.ny, ts.pathMove, ts.move, tPickTs);
            if (pointInNormRect(tpos.nx, tpos.ny, r, pad)) next.add("tspawn");
        }
        if (pointInNormRect(def.spawn.nx, def.spawn.ny, r, 0.04)) next.add("spawn");
        if (pointInNormRect(def.goal.nx, def.goal.ny, r, 0.06)) next.add("goal");
        const winds = def.winds;
        if (winds) {
            for (let i = 0; i < winds.length; i++) {
                const w = winds[i];
                const box = { x1: w.nx, x2: w.nx + w.nw, y1: w.ny, y2: w.ny + w.nh };
                if (normRectsIntersect(r, box)) next.add(`wind:${i}`);
            }
        }
        const texts = def.texts;
        if (texts) {
            for (let i = 0; i < texts.length; i++) {
                const t = texts[i];
                const box = { x1: t.nx, x2: t.nx + t.nw, y1: t.ny, y2: t.ny + t.nh };
                if (normRectsIntersect(r, box)) next.add(`txt:${i}`);
            }
        }
        const portals = def.portals;
        if (portals) {
            for (let i = 0; i < portals.length; i++) {
                const p = portals[i];
                const seg: WallSegDef = { ax: p.ax, ay: p.ay, bx: p.bx, by: p.by };
                if (segNormBBoxIntersectsRect(seg, r, pad)) next.add(portalKey(i));
            }
        }
        selectedKeys.clear();
        for (const k of next) selectedKeys.add(k);
        applyPendingLaserGroupToSelection();
    }

    function captureBulkSnap(keys: Iterable<SelKey>): BulkSnap {
        const snap: BulkSnap = {
            segs: new Map(),
            pts: new Map(),
            mvs: new Map(),
        };
        for (const k of keys) {
            if (k.startsWith("seg:")) {
                const i = Number(k.slice(4));
                const s = def.staticSegs[i];
                if (s) snap.segs.set(i, { ax: s.ax, ay: s.ay, bx: s.bx, by: s.by });
            } else if (k.startsWith("pt:")) {
                const i = Number(k.slice(3));
                const p = def.points?.[i];
                if (p)
                    snap.pts.set(i, {
                        nx: p.nx,
                        ny: p.ny,
                        move: p.move ? { tnx: p.move.tnx, tny: p.move.tny } : undefined,
                    });
            } else if (k.startsWith("mv:")) {
                const i = Number(k.slice(3));
                const m = def.moving[i];
                if (m)
                    snap.mvs.set(i, {
                        y: m.y,
                        cx: m.cx ?? 0.5,
                        move: m.move ? { ty: m.move.ty, tcx: m.move.tcx } : undefined,
                    });
            } else if (k === "spawn") snap.spawn = { ...def.spawn };
            else if (k === "goal") {
                snap.goal = {
                    nx: def.goal.nx,
                    ny: def.goal.ny,
                    move: def.goal.move ? { tnx: def.goal.move.tnx, tny: def.goal.move.tny } : undefined,
                };
            } else if (k.startsWith("ck:")) {
                const i = Number(k.slice(3));
                const c = def.checkpoints?.[i];
                if (c) {
                    if (!snap.cks) snap.cks = new Map();
                    snap.cks.set(i, {
                        nx: c.nx,
                        ny: c.ny,
                        move: c.move ? { tnx: c.move.tnx, tny: c.move.tny } : undefined,
                    });
                }
            } else if (k === "tspawn" && def.temporalSpawn) {
                const c = def.temporalSpawn;
                snap.tspawn = {
                    nx: c.nx,
                    ny: c.ny,
                    move: c.move ? { tnx: c.move.tnx, tny: c.move.tny } : undefined,
                };
            } else if (k.startsWith("wind:")) {
                const i = Number(k.slice(5));
                const w = def.winds?.[i];
                if (w) {
                    if (!snap.winds) snap.winds = new Map();
                    snap.winds.set(i, { nx: w.nx, ny: w.ny, nw: w.nw, nh: w.nh });
                }
            } else if (k.startsWith("txt:")) {
                const i = Number(k.slice(4));
                const t = def.texts?.[i];
                if (t) {
                    if (!snap.texts) snap.texts = new Map();
                    snap.texts.set(i, { nx: t.nx, ny: t.ny, nw: t.nw, nh: t.nh });
                }
            } else if (k.startsWith("portal:")) {
                const i = Number(k.slice(7));
                const p = def.portals?.[i];
                if (p) {
                    if (!snap.portals) snap.portals = new Map();
                    snap.portals.set(i, { ax: p.ax, ay: p.ay, bx: p.bx, by: p.by });
                }
            }
        }
        return snap;
    }

    function applyBulkDelta(dnx: number, dny: number, snap: BulkSnap) {
        const clampN = (v: number) => clamp(v, 0, 1);
        for (const [i, o] of snap.segs) {
            const s = def.staticSegs[i];
            if (!s) continue;
            s.ax = clampN(o.ax + dnx);
            s.ay = clampN(o.ay + dny);
            s.bx = clampN(o.bx + dnx);
            s.by = clampN(o.by + dny);
            const mv = s.move;
            if (mv) {
                mv.tax = clampN(mv.tax + dnx);
                mv.tay = clampN(mv.tay + dny);
                mv.tbx = clampN(mv.tbx + dnx);
                mv.tby = clampN(mv.tby + dny);
            }
        }
        for (const [i, o] of snap.pts) {
            const p = def.points?.[i];
            if (p) {
                p.nx = clampN(o.nx + dnx);
                p.ny = clampN(o.ny + dny);
                if (o.move && p.move) {
                    p.move.tnx = clampN(o.move.tnx + dnx);
                    p.move.tny = clampN(o.move.tny + dny);
                }
            }
        }
        for (const [i, o] of snap.mvs) {
            const m = def.moving[i];
            if (!m) continue;
            m.y = clampN(o.y + dny);
            const ncx = clampN(o.cx + dnx);
            if (Math.abs(ncx - 0.5) < 1e-7) delete m.cx;
            else m.cx = ncx;
            if (o.move && m.move) {
                m.move.ty = clampN(o.move.ty + dny);
                m.move.tcx = clampN(o.move.tcx + dnx);
            }
        }
        if (snap.spawn) {
            def.spawn.nx = clampN(snap.spawn.nx + dnx);
            def.spawn.ny = clampN(snap.spawn.ny + dny);
        }
        if (snap.goal) {
            def.goal.nx = clampN(snap.goal.nx + dnx);
            def.goal.ny = clampN(snap.goal.ny + dny);
            if (snap.goal.move && def.goal.move) {
                def.goal.move.tnx = clampN(snap.goal.move.tnx + dnx);
                def.goal.move.tny = clampN(snap.goal.move.tny + dny);
            }
        }
        if (snap.cks && def.checkpoints) {
            for (const [i, o] of snap.cks) {
                const ck = def.checkpoints[i];
                if (!ck) continue;
                ck.nx = clampN(o.nx + dnx);
                ck.ny = clampN(o.ny + dny);
                if (o.move && ck.move) {
                    ck.move.tnx = clampN(o.move.tnx + dnx);
                    ck.move.tny = clampN(o.move.tny + dny);
                }
            }
        }
        if (snap.tspawn && def.temporalSpawn) {
            def.temporalSpawn.nx = clampN(snap.tspawn.nx + dnx);
            def.temporalSpawn.ny = clampN(snap.tspawn.ny + dny);
            if (snap.tspawn.move && def.temporalSpawn.move) {
                def.temporalSpawn.move.tnx = clampN(snap.tspawn.move.tnx + dnx);
                def.temporalSpawn.move.tny = clampN(snap.tspawn.move.tny + dny);
            }
        }
        if (snap.winds && def.winds) {
            for (const [i, o] of snap.winds) {
                const w = def.winds[i];
                if (!w) continue;
                w.nx = clampN(o.nx + dnx);
                w.ny = clampN(o.ny + dny);
                w.nw = clamp(o.nw, 0.004, Math.max(0.004, 1 - w.nx));
                w.nh = clamp(o.nh, 0.004, Math.max(0.004, 1 - w.ny));
            }
        }
        if (snap.texts && def.texts) {
            for (const [i, o] of snap.texts) {
                const t = def.texts[i];
                if (!t) continue;
                t.nx = clampN(o.nx + dnx);
                t.ny = clampN(o.ny + dny);
                t.nw = clamp(o.nw, 0.012, Math.max(0.012, 1 - t.nx));
                t.nh = clamp(o.nh, 0.012, Math.max(0.012, 1 - t.ny));
            }
        }
        if (snap.portals && def.portals) {
            for (const [i, o] of snap.portals) {
                const p = def.portals[i];
                if (!p) continue;
                p.ax = clampN(o.ax + dnx);
                p.ay = clampN(o.ay + dny);
                p.bx = clampN(o.bx + dnx);
                p.by = clampN(o.by + dny);
            }
        }
    }

    function laserCenterNormForCentroid(i: number, tSec: number) {
        const seg = laserSegNorm(def.moving[i], tSec);
        return { nx: (seg.x1 + seg.x2) / 2, ny: (seg.y1 + seg.y2) / 2 };
    }

    function computeSelectionCentroidNorm(keysInput: SelKey[]): { nx: number; ny: number } | null {
        const tPick = pauseAnimationsCheck.checked ? 0 : performance.now() / 1000;
        const acc: { nx: number; ny: number }[] = [];
        for (const k of keysInput) {
            if (k.startsWith("seg:")) {
                const i = Number(k.slice(4));
                const s = def.staticSegs[i];
                if (s) acc.push({ nx: (s.ax + s.bx) / 2, ny: (s.ay + s.by) / 2 });
            } else if (k.startsWith("pt:")) {
                const i = Number(k.slice(3));
                const p = def.points?.[i];
                if (p) acc.push({ nx: p.nx, ny: p.ny });
            } else if (k.startsWith("mv:")) {
                const i = Number(k.slice(3));
                if (def.moving[i]) acc.push(laserCenterNormForCentroid(i, tPick));
            } else if (k === "goal") {
                acc.push({ nx: def.goal.nx, ny: def.goal.ny });
            } else if (k.startsWith("ck:")) {
                const i = Number(k.slice(3));
                const ck = def.checkpoints?.[i];
                if (ck) acc.push({ nx: ck.nx, ny: ck.ny });
            } else if (k === "tspawn" && def.temporalSpawn) {
                acc.push({ nx: def.temporalSpawn.nx, ny: def.temporalSpawn.ny });
            } else if (k.startsWith("wind:")) {
                const i = Number(k.slice(5));
                const w = def.winds?.[i];
                if (w) acc.push({ nx: w.nx + w.nw * 0.5, ny: w.ny + w.nh * 0.5 });
            } else if (k.startsWith("txt:")) {
                const i = Number(k.slice(4));
                const t = def.texts?.[i];
                if (t) acc.push({ nx: t.nx + t.nw * 0.5, ny: t.ny + t.nh * 0.5 });
            } else if (k.startsWith("portal:")) {
                const i = Number(k.slice(7));
                const p = def.portals?.[i];
                if (p) acc.push({ nx: (p.ax + p.bx) / 2, ny: (p.ay + p.by) / 2 });
            }
        }
        if (!acc.length) return null;
        const sx = acc.reduce((s, p) => s + p.nx, 0) / acc.length;
        const sy = acc.reduce((s, p) => s + p.ny, 0) / acc.length;
        return { nx: sx, ny: sy };
    }

    function entityCentroidForKey(k: SelKey, tSec: number): { nx: number; ny: number } | null {
        if (k.startsWith("seg:")) {
            const i = Number(k.slice(4));
            const s = def.staticSegs[i];
            if (!s) return null;
            return { nx: (s.ax + s.bx) / 2, ny: (s.ay + s.by) / 2 };
        }
        if (k.startsWith("pt:")) {
            const i = Number(k.slice(3));
            const p = def.points?.[i];
            if (!p) return null;
            return { nx: p.nx, ny: p.ny };
        }
        if (k.startsWith("mv:")) {
            const i = Number(k.slice(3));
            if (!def.moving[i]) return null;
            return laserCenterNormForCentroid(i, tSec);
        }
        if (k === "goal") return { nx: def.goal.nx, ny: def.goal.ny };
        if (k.startsWith("ck:")) {
            const i = Number(k.slice(3));
            const ck = def.checkpoints?.[i];
            if (ck) return { nx: ck.nx, ny: ck.ny };
        }
        if (k === "tspawn" && def.temporalSpawn) return { nx: def.temporalSpawn.nx, ny: def.temporalSpawn.ny };
        if (k.startsWith("portal:")) {
            const i = Number(k.slice(7));
            const p = def.portals?.[i];
            if (p) return { nx: (p.ax + p.bx) / 2, ny: (p.ay + p.by) / 2 };
        }
        if (k.startsWith("wind:")) {
            const i = Number(k.slice(5));
            const w = def.winds?.[i];
            if (!w) return null;
            return { nx: w.nx + w.nw * 0.5, ny: w.ny + w.nh * 0.5 };
        }
        return null;
    }

    function commitMovePathDraft() {
        if (!movePathDraft || movePathDraft.anchors.length < 1) return;
        const t0 = pauseAnimationsCheck.checked ? 0 : performance.now() / 1000;
        const outS = clamp(Number(admMoveOut.value || 1.2), 0.05, 120);
        const backS = clamp(Number(admMoveBack.value || 1.2), 0.05, 120);
        const loop = pathLoopCheck.checked && movePathDraft.anchors.length >= 2;
        const R = movePathDraft.groupRest;
        const axs = movePathDraft.anchors.map((a) => ({ nx: clamp(a.nx, 0, 1), ny: clamp(a.ny, 0, 1) }));
        const clampN = (v: number) => clamp(v, 0, 1);
        for (const k of movePathDraft.targetKeys) {
            const C = entityCentroidForKey(k, t0);
            if (!C) continue;
            const dnx = C.nx - R.nx;
            const dny = C.ny - R.ny;
            const anchors = axs.map((p) => ({ nx: clampN(p.nx + dnx), ny: clampN(p.ny + dny) }));
            const pm: OscPathMove = {
                restNx: clampN(C.nx),
                restNy: clampN(C.ny),
                anchors,
                loop,
                legOutS: outS,
                legBackS: backS,
            };
            if (k.startsWith("seg:")) {
                const i = Number(k.slice(4));
                const s = def.staticSegs[i];
                if (!s) continue;
                delete s.move;
                s.pathMove = pm;
            } else if (k.startsWith("mv:")) {
                const i = Number(k.slice(3));
                const m = def.moving[i];
                if (!m) continue;
                delete m.move;
                m.pathMove = pm;
            } else if (k.startsWith("pt:")) {
                const i = Number(k.slice(3));
                const p = def.points?.[i];
                if (!p) continue;
                delete p.move;
                p.pathMove = pm;
            } else if (k === "goal") {
                delete def.goal.move;
                def.goal.pathMove = pm;
            } else if (k.startsWith("ck:")) {
                const i = Number(k.slice(3));
                const p = def.checkpoints?.[i];
                if (!p) continue;
                delete p.move;
                p.pathMove = pm;
            } else if (k === "tspawn" && def.temporalSpawn) {
                delete def.temporalSpawn.move;
                def.temporalSpawn.pathMove = pm;
            }
        }
        clearMovePathDraft();
        pendingMovePick = null;
        syncPathDraftUi();
        paint();
        maybeRecordHistory();
    }

    pathDoneBtn.onclick = () => commitMovePathDraft();

    const deleteBulkDropdownWrap = document.createElement("div");
    deleteBulkDropdownWrap.className = "adminToolDropdown adminDeleteBulkDropdown";
    const deleteBulkBtn = document.createElement("button");
    deleteBulkBtn.type = "button";
    deleteBulkBtn.className = "btn adminToolDropdownBtn";
    deleteBulkBtn.textContent = "Delete all ▾";
    const deleteBulkMenu = document.createElement("div");
    deleteBulkMenu.className = "adminToolDropdownMenu hidden";
    const mkDeleteAllItem = (label: string, onPick: () => void) => {
        const it = document.createElement("button");
        it.type = "button";
        it.className = "btn adminToolDropdownItem";
        it.textContent = label;
        it.onclick = (ev) => {
            ev.stopPropagation();
            deleteBulkMenu.classList.add("hidden");
            onPick();
        };
        deleteBulkMenu.appendChild(it);
    };
    const pruneKeysNotMatching = (pred: (k: SelKey) => boolean) => {
        const next = new Set<SelKey>();
        for (const k of selectedKeys) {
            if (pred(k)) next.add(k);
        }
        selectedKeys.clear();
        for (const k of next) selectedKeys.add(k);
    };
    mkDeleteAllItem("Delete all lasers", () => {
        if (!def.staticSegs.some((s) => !s.solidWall) && !def.moving.length) return;
        if (!confirm("Remove all laser segments and moving lasers?")) return;
        def.staticSegs = def.staticSegs.filter((s) => s.solidWall);
        def.moving = [];
        pruneKeysNotMatching((k) => !k.startsWith("mv:") && !k.startsWith("seg:"));
        selectedSeg = -1;
        pendingMovePick = null;
        movePathDraft = null;
        syncPathDraftUi();
        paint();
        maybeRecordHistory();
    });
    mkDeleteAllItem("Delete all walls", () => {
        if (!def.staticSegs.some((s) => s.solidWall)) return;
        if (!confirm("Remove all solid walls?")) return;
        def.staticSegs = def.staticSegs.filter((s) => !s.solidWall);
        pruneKeysNotMatching((k) => !k.startsWith("seg:"));
        selectedSeg = -1;
        paint();
        maybeRecordHistory();
    });
    mkDeleteAllItem("Delete all points", () => {
        if (!def.points?.length) return;
        if (!confirm("Delete all points?")) return;
        def.points = undefined;
        pruneKeysNotMatching((k) => !k.startsWith("pt:"));
        movePathDraft = null;
        syncPathDraftUi();
        paint();
        maybeRecordHistory();
    });
    mkDeleteAllItem("Delete all checkpoints", () => {
        if (!def.checkpoints?.length) return;
        if (!confirm("Delete all checkpoints?")) return;
        def.checkpoints = undefined;
        pruneKeysNotMatching((k) => !k.startsWith("ck:"));
        paint();
        maybeRecordHistory();
    });
    mkDeleteAllItem("Delete all wind", () => {
        if (!def.winds?.length) return;
        if (!confirm("Delete all wind zones?")) return;
        def.winds = undefined;
        pruneKeysNotMatching((k) => !k.startsWith("wind:"));
        paint();
        maybeRecordHistory();
    });
    mkDeleteAllItem("Delete all text", () => {
        if (!def.texts?.length) return;
        if (!confirm("Delete all text labels?")) return;
        def.texts = undefined;
        pruneKeysNotMatching((k) => !k.startsWith("txt:"));
        paint();
        maybeRecordHistory();
    });
    mkDeleteAllItem("Delete all portals", () => {
        if (!def.portals?.length) return;
        if (!confirm("Delete all portals?")) return;
        def.portals = undefined;
        pruneKeysNotMatching((k) => !k.startsWith("portal:"));
        paint();
        maybeRecordHistory();
    });
    mkDeleteAllItem("Delete temporal spawn", () => {
        if (!def.temporalSpawn) return;
        delete def.temporalSpawn;
        selectedKeys.delete("tspawn");
        paint();
        maybeRecordHistory();
    });
    mkDeleteAllItem("Delete everything (except Spawn & Goal)", () => {
        const has =
            def.staticSegs.length ||
            def.moving.length ||
            def.points?.length ||
            def.checkpoints?.length ||
            def.winds?.length ||
            def.texts?.length ||
            def.temporalSpawn ||
            def.portals?.length;
        if (!has) return;
        if (!confirm("Delete all objects except spawn and goal?")) return;
        def.staticSegs = [];
        def.moving = [];
        def.points = undefined;
        def.checkpoints = undefined;
        def.winds = undefined;
        def.texts = undefined;
        def.portals = undefined;
        delete def.temporalSpawn;
        selectedSeg = -1;
        pendingMovePick = null;
        movePathDraft = null;
        syncPathDraftUi();
        clearSelectionState();
        paint();
        maybeRecordHistory();
    });
    deleteBulkBtn.onclick = (ev) => {
        ev.stopPropagation();
        deleteBulkMenu.classList.toggle("hidden");
        positionAdminDropdownMenu(deleteBulkMenu, deleteBulkBtn);
    };
    document.addEventListener("click", (ev) => {
        const t = ev.target as Node | null;
        if (t && !deleteBulkDropdownWrap.contains(t)) {
            deleteBulkMenu.classList.add("hidden");
            positionAdminDropdownMenu(deleteBulkMenu, deleteBulkBtn);
        }
    });
    deleteBulkDropdownWrap.appendChild(deleteBulkBtn);
    deleteBulkDropdownWrap.appendChild(deleteBulkMenu);
    rowDeleteBulk.appendChild(temporalSpawnBtn);
    rowDeleteBulk.appendChild(deleteBulkDropdownWrap);

    function selectionHasLaserTargets(): boolean {
        for (const k of selectedKeys) {
            if (k.startsWith("seg:") || k.startsWith("mv:")) return true;
        }
        return false;
    }

    function applyLaserGroupFromSelect() {
        if (activeTool !== "select" || !selectionHasLaserTargets()) return;
        const raw = selectLaserGroupSelect.value;
        let changed = false;
        if (raw === "") {
            for (const k of selectedKeys) {
                if (k.startsWith("seg:")) {
                    const i = Number(k.slice(4));
                    const s = def.staticSegs[i];
                    if (s && s.toggleGroup !== undefined) {
                        delete s.toggleGroup;
                        delete s.togglePointType;
                        changed = true;
                    }
                } else if (k.startsWith("mv:")) {
                    const i = Number(k.slice(3));
                    const m = def.moving[i];
                    if (m && m.toggleGroup !== undefined) {
                        delete m.toggleGroup;
                        delete m.togglePointType;
                        changed = true;
                    }
                }
            }
        } else {
            const gv = Math.floor(Number(raw));
            if (!Number.isFinite(gv) || gv < 1 || gv > 15) return;
            const gvn = gv as SpeedrunToggleGroup;
            const ptType = readPointTypeSelect(selectLaserPointTypeSelect);
            for (const k of selectedKeys) {
                if (k.startsWith("seg:")) {
                    const i = Number(k.slice(4));
                    const s = def.staticSegs[i];
                    if (!s) continue;
                    if (s.toggleGroup !== gvn) {
                        s.toggleGroup = gvn;
                        changed = true;
                    }
                    const prevType = s.togglePointType ?? "circle";
                    if (prevType !== ptType) {
                        applyTogglePointType(s, ptType);
                        changed = true;
                    }
                } else if (k.startsWith("mv:")) {
                    const i = Number(k.slice(3));
                    const m = def.moving[i];
                    if (!m) continue;
                    if (m.toggleGroup !== gvn) {
                        m.toggleGroup = gvn;
                        changed = true;
                    }
                    const prevType = m.togglePointType ?? "circle";
                    if (prevType !== ptType) {
                        applyTogglePointType(m, ptType);
                        changed = true;
                    }
                }
            }
        }
        if (changed) {
            paint();
            maybeRecordHistory();
        }
    }

    function applyPendingLaserGroupToSelection() {
        if (activeTool !== "select" || !selectionHasLaserTargets()) return;
        if (selectLaserGroupSelect.value === "") return;
        applyLaserGroupFromSelect();
    }

    selectLaserGroupSelect.addEventListener("change", () => {
        applyLaserGroupFromSelect();
    });
    selectLaserPointTypeSelect.addEventListener("change", () => {
        if (selectLaserGroupSelect.value === "") return;
        applyLaserGroupFromSelect();
    });

    function paint() {
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        const rw = canvas.width / dpr;
        const rh = canvas.height / dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.fillStyle = "#070712";
        ctx.fillRect(0, 0, rw, rh);
        const tSec = performance.now() / 1000;
        const tAnim = pauseAnimationsCheck.checked ? 0 : tSec;
        const hidePaths = hidePathCheck.checked;

        if (def.winds) {
            for (let i = 0; i < def.winds.length; i++) {
                const wz = def.winds[i];
                const x = wz.nx * rw;
                const y = wz.ny * rh;
                const ww = wz.nw * rw;
                const hh = wz.nh * rh;
                const selW = selectedKeys.has(`wind:${i}`);
                ctx.fillStyle = selW ? "rgba(120,230,255,0.22)" : "rgba(100,200,255,0.11)";
                ctx.fillRect(x, y, ww, hh);
                ctx.strokeStyle = selW ? "rgba(160,250,255,0.78)" : "rgba(120,200,255,0.42)";
                ctx.lineWidth = selW ? 2 : 1;
                ctx.setLineDash([6, 4]);
                ctx.strokeRect(x, y, ww, hh);
                ctx.setLineDash([]);
                const u = speedrunWindDirUnit(wz.dir);
                const cx = x + ww / 2;
                const cy = y + hh / 2;
                const alen = Math.min(ww, hh) * 0.24;
                ctx.strokeStyle = "rgba(210,245,255,0.88)";
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(cx - u.x * alen * 0.3, cy - u.y * alen * 0.3);
                ctx.lineTo(cx + u.x * alen, cy + u.y * alen);
                ctx.stroke();
            }
        }

        for (let i = 0; i < def.staticSegs.length; i++) {
            const s = def.staticSegs[i];
            const p = wallSegToPx(s, tAnim, rw, rh);
            const a = wallDrawAlpha(s, false, false);
            const selWall = selectedKeys.has(`seg:${i}`);
            const tg = s.toggleGroup;
            let strokeCol: string;
            if (s.solidWall) {
                strokeCol =
                    i === selectedSeg ? "#44ff88" : selWall ? "#66eeff" : `rgba(105,110,128,${a})`;
                if (!selWall && i !== selectedSeg && tg != null && typeof tg === "number") {
                    const { r, g, b } = speedrunGroupRgb(tg);
                    strokeCol = `rgba(${Math.round(r * 0.42 + 78)},${Math.round(g * 0.42 + 78)},${Math.round(
                        b * 0.42 + 82,
                    )},${a})`;
                }
            } else {
                strokeCol =
                    i === selectedSeg ? "#44ff88" : selWall ? "#66eeff" : `rgba(255,55,55,${a})`;
                if (!selWall && i !== selectedSeg && tg != null && typeof tg === "number") {
                    const { r, g, b } = speedrunGroupRgb(tg);
                    strokeCol = `rgba(${r},${g},${b},${a})`;
                }
            }
            ctx.strokeStyle = strokeCol;
            const lwBase = speedrunLaserHalfPxForWall(s) * 2;
            ctx.lineWidth = i === selectedSeg || selWall ? Math.max(lwBase + 1.5, lwBase * 1.12) : lwBase;
            ctx.beginPath();
            ctx.moveTo(p.x1, p.y1);
            ctx.lineTo(p.x2, p.y2);
            ctx.stroke();

            if (!hidePaths && s.move) {
                const tp = wallSegToPx(
                    {
                        ...s,
                        ax: s.move.tax,
                        ay: s.move.tay,
                        bx: s.move.tbx,
                        by: s.move.tby,
                        move: undefined,
                        rotate: undefined,
                    },
                    0,
                    rw,
                    rh,
                );
                ctx.globalAlpha = 0.35 * a;
                ctx.setLineDash([5, 5]);
                ctx.strokeStyle = "#44ff88";
                ctx.beginPath();
                ctx.moveTo(p.x1, p.y1);
                ctx.lineTo(tp.x1, tp.y1);
                ctx.moveTo(p.x2, p.y2);
                ctx.lineTo(tp.x2, tp.y2);
                ctx.stroke();
                ctx.setLineDash([]);
                ctx.globalAlpha = 1;
            }
            if (!hidePaths && s.pathMove && s.pathMove.anchors.length) {
                const pm = s.pathMove;
                const pts = [{ nx: pm.restNx, ny: pm.restNy }, ...pm.anchors];
                ctx.globalAlpha = 0.55 * a;
                ctx.setLineDash([4, 4]);
                ctx.strokeStyle = "#88ddff";
                ctx.beginPath();
                ctx.moveTo(pts[0].nx * rw, pts[0].ny * rh);
                for (let pi = 1; pi < pts.length; pi++) ctx.lineTo(pts[pi].nx * rw, pts[pi].ny * rh);
                ctx.stroke();
                ctx.setLineDash([]);
                ctx.globalAlpha = 1;
            }
            if (s.rotate) {
                ctx.globalAlpha = 0.25 * a;
                ctx.strokeStyle = "#ffee88";
                const mx = (p.x1 + p.x2) / 2;
                const my = (p.y1 + p.y2) / 2;
                ctx.beginPath();
                ctx.arc(mx, my, 22, 0, Math.PI * 1.5);
                ctx.stroke();
                ctx.globalAlpha = 1;
            }
        }

        for (let i = 0; i < def.moving.length; i++) {
            const mv = def.moving[i];
            const seg = laserSegNorm(mv, tAnim);
            const px1 = seg.x1 * rw;
            const py1 = seg.y1 * rh;
            const px2 = seg.x2 * rw;
            const py2 = seg.y2 * rh;
            const selL = selectedKeys.has(`mv:${i}`);
            const tgMv = mv.toggleGroup;
            let strokeLas = selL ? "#aaffff" : "#ff9944";
            let shadowLas = selL ? "#66ffff" : "rgba(255,120,40,0.5)";
            if (!selL && tgMv != null && typeof tgMv === "number") {
                const { r, g, b } = speedrunGroupRgb(tgMv);
                strokeLas = `rgb(${r},${g},${b})`;
                shadowLas = `rgba(${r},${g},${b},0.65)`;
            }
            ctx.strokeStyle = strokeLas;
            const lwMv = speedrunLaserHalfPxForMoving(mv) * 2;
            ctx.lineWidth = selL ? Math.max(lwMv + 1.5, lwMv * 1.12) : lwMv;
            ctx.shadowColor = shadowLas;
            ctx.shadowBlur = selL ? 14 : 8;
            ctx.beginPath();
            ctx.moveTo(px1, py1);
            ctx.lineTo(px2, py2);
            ctx.stroke();
            ctx.shadowBlur = 0;
            if (!hidePaths && mv.pathMove && mv.pathMove.anchors.length) {
                const pm = mv.pathMove;
                const pts = [{ nx: pm.restNx, ny: pm.restNy }, ...pm.anchors];
                ctx.globalAlpha = 0.55;
                ctx.setLineDash([4, 4]);
                ctx.strokeStyle = "#88ddff";
                ctx.beginPath();
                ctx.moveTo(pts[0].nx * rw, pts[0].ny * rh);
                for (let pi = 1; pi < pts.length; pi++) ctx.lineTo(pts[pi].nx * rw, pts[pi].ny * rh);
                ctx.stroke();
                ctx.setLineDash([]);
                ctx.globalAlpha = 1;
            }
            if (!hidePaths && mv.move && mv.move.outS > 0.01 && mv.move.backS > 0.01) {
                const T = mv.move.outS + mv.move.backS;
                const tPeak = mv.move.outS + T * 32;
                const s1 = movingLaserSegNorm(mv, tPeak);
                ctx.globalAlpha = 0.38;
                ctx.setLineDash([5, 5]);
                ctx.strokeStyle = "#44ff88";
                ctx.beginPath();
                ctx.moveTo(px1, py1);
                ctx.lineTo(s1.ax * rw, s1.ay * rh);
                ctx.moveTo(px2, py2);
                ctx.lineTo(s1.bx * rw, s1.by * rh);
                ctx.stroke();
                ctx.setLineDash([]);
                ctx.globalAlpha = 1;
            }
        }

        for (let i = 0; i < (def.points?.length || 0); i++) {
            const pt = def.points![i];
            const pos = speedrunPointGoalPosNorm(pt.nx, pt.ny, pt.pathMove, pt.move, tAnim);
            const px = pos.nx * rw;
            const py = pos.ny * rh;
            const bob = pauseAnimationsCheck.checked ? 0 : Math.sin(tSec * 3 + i) * 4;
            const selP = selectedKeys.has(`pt:${i}`);
            const pg = pt.group;
            let fillC = "#fff25a";
            let shC = selP ? "#66ffff" : "#ffe500";
            if (pg != null && typeof pg === "number") {
                const { r, g, b } = speedrunGroupRgb(pg);
                fillC = `rgb(${r},${g},${b})`;
                shC = selP ? "#99eeff" : pg === 7 ? "rgba(210,220,255,0.95)" : `rgba(${r},${g},${b},0.9)`;
            }
            ctx.fillStyle = fillC;
            ctx.shadowColor = shC;
            ctx.shadowBlur = selP ? 22 : 16;
            const rPt = Math.max(5, Math.min(22, 10 * (pt.radiusMul ?? 1)));
            drawSpeedrunPointShape(ctx, px, py + bob, selP ? rPt + 2 : rPt, speedrunPointTypeOf(pt), pt.rotationDeg ?? 0);
            ctx.shadowBlur = 0;
            if (selP) {
                ctx.strokeStyle = "#66ffff";
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.arc(px, py + bob, rPt + 4, 0, Math.PI * 2);
                ctx.stroke();
            }
            if (!hidePaths && pt.pathMove && pt.pathMove.anchors.length) {
                const pm = pt.pathMove;
                const pts = [{ nx: pm.restNx, ny: pm.restNy }, ...pm.anchors];
                ctx.globalAlpha = 0.5;
                ctx.setLineDash([4, 4]);
                ctx.strokeStyle = "#88ddff";
                ctx.beginPath();
                ctx.moveTo(pts[0].nx * rw, pts[0].ny * rh + bob);
                for (let pi = 1; pi < pts.length; pi++) ctx.lineTo(pts[pi].nx * rw, pts[pi].ny * rh + bob);
                ctx.stroke();
                ctx.setLineDash([]);
                ctx.globalAlpha = 1;
            }
            if (!hidePaths && pt.move && pt.move.outS > 0.01 && pt.move.backS > 0.01) {
                ctx.globalAlpha = 0.4;
                ctx.setLineDash([5, 5]);
                ctx.strokeStyle = "#44ff88";
                ctx.beginPath();
                ctx.moveTo(pt.nx * rw, pt.ny * rh + bob);
                ctx.lineTo(pt.move.tnx * rw, pt.move.tny * rh + bob);
                ctx.stroke();
                ctx.setLineDash([]);
                ctx.globalAlpha = 1;
            }
        }

        const cks = def.checkpoints ?? [];
        for (let i = 0; i < cks.length; i++) {
            const c = cks[i];
            const cpos = speedrunPointGoalPosNorm(c.nx, c.ny, c.pathMove, c.move, tAnim);
            const px = cpos.nx * rw;
            const py = cpos.ny * rh;
            const selC = selectedKeys.has(ckKey(i));
            const ckBob = pauseAnimationsCheck.checked ? 0 : Math.sin(tSec * 3 + i * 0.7) * 4;
            const ckR = speedrunCheckpointRadiusPx(c.sizeMul, 1);
            ctx.shadowColor = selC ? "rgba(120,255,200,0.95)" : "rgba(45,110,75,0.55)";
            ctx.shadowBlur = selC ? 22 : 12;
            drawSpeedrunCheckpointDeltoid(ctx, px, py + ckBob, ckR, false, c.rotationDeg ?? 0);
            ctx.shadowBlur = 0;
            if (selC) {
                ctx.strokeStyle = "#66ffff";
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.arc(px, py + ckBob, ckR + 5, 0, Math.PI * 2);
                ctx.stroke();
            }
            if (!hidePaths && c.pathMove && c.pathMove.anchors.length) {
                const pm = c.pathMove;
                const pts = [{ nx: pm.restNx, ny: pm.restNy }, ...pm.anchors];
                ctx.globalAlpha = 0.5;
                ctx.setLineDash([4, 4]);
                ctx.strokeStyle = "#88ddff";
                ctx.beginPath();
                ctx.moveTo(pts[0].nx * rw, pts[0].ny * rh);
                for (let pi = 1; pi < pts.length; pi++) ctx.lineTo(pts[pi].nx * rw, pts[pi].ny * rh);
                ctx.stroke();
                ctx.setLineDash([]);
                ctx.globalAlpha = 1;
            }
            if (!hidePaths && c.move && c.move.outS > 0.01 && c.move.backS > 0.01) {
                ctx.globalAlpha = 0.4;
                ctx.setLineDash([5, 5]);
                ctx.strokeStyle = "#44ff88";
                ctx.beginPath();
                ctx.moveTo(c.nx * rw, c.ny * rh);
                ctx.lineTo(c.move.tnx * rw, c.move.tny * rh);
                ctx.stroke();
                ctx.setLineDash([]);
                ctx.globalAlpha = 1;
            }
        }

        const portalLs = 1;
        if (!pauseAnimationsCheck.checked && (def.portals?.length ?? 0) > 0) {
            updateSpeedrunPortalFx(def.portals!, rw, rh, 1, portalLs);
        }
        drawSpeedrunPortals(ctx, def.portals ?? [], rw, rh, tSec, {
            lineScale: portalLs,
            selectedKeys,
            portalKey,
        });

        if (def.temporalSpawn) {
            const ts = def.temporalSpawn;
            const tpos = speedrunPointGoalPosNorm(ts.nx, ts.ny, ts.pathMove, ts.move, tAnim);
            const px = tpos.nx * rw;
            const py = tpos.ny * rh;
            const selT = selectedKeys.has("tspawn");
            const tsBob = pauseAnimationsCheck.checked ? 0 : Math.sin(tSec * 3 + 1.3) * 3;
            const tsR = Math.max(7, Math.min(18, 10));
            ctx.save();
            ctx.setLineDash([5, 4]);
            ctx.strokeStyle = selT ? "#66ffff" : "#44ccff";
            ctx.lineWidth = selT ? 2.5 : 2;
            ctx.shadowColor = selT ? "rgba(120,220,255,0.9)" : "rgba(40,160,220,0.5)";
            ctx.shadowBlur = selT ? 18 : 10;
            ctx.strokeRect(px - tsR, py + tsBob - tsR, tsR * 2, tsR * 2);
            ctx.setLineDash([]);
            ctx.fillStyle = "rgba(30,120,180,0.35)";
            ctx.fillRect(px - tsR * 0.6, py + tsBob - tsR * 0.6, tsR * 1.2, tsR * 1.2);
            ctx.restore();
            if (!hidePaths && ts.pathMove && ts.pathMove.anchors.length) {
                const pm = ts.pathMove;
                const pts = [{ nx: pm.restNx, ny: pm.restNy }, ...pm.anchors];
                ctx.globalAlpha = 0.5;
                ctx.setLineDash([4, 4]);
                ctx.strokeStyle = "#88ddff";
                ctx.beginPath();
                ctx.moveTo(pts[0].nx * rw, pts[0].ny * rh);
                for (let pi = 1; pi < pts.length; pi++) ctx.lineTo(pts[pi].nx * rw, pts[pi].ny * rh);
                ctx.stroke();
                ctx.setLineDash([]);
                ctx.globalAlpha = 1;
            }
        }

        const g = def.goal;
        const m = Math.min(rw, rh);
        const gh = g.nHalf * m;
        const gpos = speedrunPointGoalPosNorm(g.nx, g.ny, g.pathMove, g.move, tAnim);
        const gx = gpos.nx * rw;
        const gy = gpos.ny * rh;
        ctx.fillStyle = "#ff66ee";
        ctx.save();
        ctx.translate(gx, gy);
        if (g.rotationDeg) ctx.rotate((g.rotationDeg * Math.PI) / 180);
        ctx.fillRect(-gh, -gh, gh * 2, gh * 2);
        ctx.restore();
        if (!hidePaths && g.pathMove && g.pathMove.anchors.length) {
            const pm = g.pathMove;
            const pts = [{ nx: pm.restNx, ny: pm.restNy }, ...pm.anchors];
            ctx.globalAlpha = 0.5;
            ctx.setLineDash([4, 4]);
            ctx.strokeStyle = "#88ddff";
            ctx.beginPath();
            ctx.moveTo(pts[0].nx * rw, pts[0].ny * rh);
            for (let pi = 1; pi < pts.length; pi++) ctx.lineTo(pts[pi].nx * rw, pts[pi].ny * rh);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.globalAlpha = 1;
        }
        if (!hidePaths && g.move && g.move.outS > 0.01 && g.move.backS > 0.01) {
            ctx.globalAlpha = 0.4;
            ctx.setLineDash([5, 5]);
            ctx.strokeStyle = "#44ff88";
            ctx.beginPath();
            ctx.moveTo(g.nx * rw, g.ny * rh);
            ctx.lineTo(g.move.tnx * rw, g.move.tny * rh);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.globalAlpha = 1;
        }
        if (selectedKeys.has("goal")) {
            ctx.strokeStyle = "#66ffff";
            ctx.lineWidth = 3;
            ctx.strokeRect(gx - gh - 4, gy - gh - 4, gh * 2 + 8, gh * 2 + 8);
        }
        const sp = def.spawn;
        const sx = sp.nx * rw;
        const sy = sp.ny * rh;
        ctx.strokeStyle = "#00ffff";
        ctx.lineWidth = 2;
        ctx.strokeRect(sx - 11, sy - 11, 22, 22);
        if (selectedKeys.has("spawn")) {
            ctx.strokeStyle = "#66ffff";
            ctx.lineWidth = 3;
            ctx.strokeRect(sx - 14, sy - 14, 28, 28);
        }
        const bs = def.ballSpawn;
        const bx = bs.nx * rw;
        const by = bs.ny * rh;
        ctx.fillStyle = "rgba(255,200,80,0.85)";
        ctx.beginPath();
        ctx.arc(bx, by, 10, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "#ffcc44";
        ctx.lineWidth = 2;
        ctx.stroke();
        if (selectedKeys.has("ballSpawn")) {
            ctx.strokeStyle = "#66ffff";
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.arc(bx, by, 14, 0, Math.PI * 2);
            ctx.stroke();
        }

        if (def.texts) {
            for (let i = 0; i < def.texts.length; i++) {
                const t = def.texts[i];
                const selT = selectedKeys.has(`txt:${i}`);
                const x = t.nx * rw;
                const y = t.ny * rh;
                const ww = t.nw * rw;
                const hh = t.nh * rh;
                if (selT) {
                    ctx.strokeStyle = "rgba(120,255,220,0.85)";
                    ctx.lineWidth = 2;
                    ctx.setLineDash([5, 4]);
                    ctx.strokeRect(x, y, ww, hh);
                    ctx.setLineDash([]);
                }
                drawSpeedrunTextLabel(ctx, t, rw, rh);
            }
        }

        if (selectionRotateMode && activeTool === "select") {
            for (const target of getRotatableTargets()) {
                drawRotateWheelUi(ctx, rotateTargetHandlePx(target, rw, rh, tAnim));
            }
        }

        if (dragLineStart && squareStart === null && !circleStart && (LASER_STROKE_TOOLS.has(activeTool) || SOLID_WALL_STROKE_TOOLS.has(activeTool))) {
            const ptr = lastPtrNorm;
            if (ptr) {
                ctx.setLineDash([4, 4]);
                ctx.strokeStyle = "rgba(255,255,255,0.35)";
                ctx.beginPath();
                ctx.moveTo(dragLineStart.nx * rw, dragLineStart.ny * rh);
                ctx.lineTo(ptr.nx * rw, ptr.ny * rh);
                ctx.stroke();
                ctx.setLineDash([]);
            }
        }
        if (portalDragStart && lastPtrNorm && activeTool === "portal") {
            const group = readPortalGroupPick();
            const side = readPortalSidePick();
            const color = speedrunPortalColor(group, side);
            ctx.strokeStyle = color;
            ctx.shadowColor = color;
            ctx.shadowBlur = 14;
            ctx.lineWidth = 9;
            ctx.lineCap = "round";
            ctx.beginPath();
            ctx.moveTo(portalDragStart.nx * rw, portalDragStart.ny * rh);
            ctx.lineTo(lastPtrNorm.nx * rw, lastPtrNorm.ny * rh);
            ctx.stroke();
            ctx.shadowBlur = 0;
        }
        if (squareStart && lastPtrNorm && (LASER_STROKE_TOOLS.has(activeTool) || SOLID_WALL_STROKE_TOOLS.has(activeTool))) {
            const nx1 = Math.min(squareStart.nx, lastPtrNorm.nx);
            const nx2 = Math.max(squareStart.nx, lastPtrNorm.nx);
            const ny1 = Math.min(squareStart.ny, lastPtrNorm.ny);
            const ny2 = Math.max(squareStart.ny, lastPtrNorm.ny);
            const wPx = (nx2 - nx1) * rw;
            const hPx = (ny2 - ny1) * rh;
            ctx.setLineDash([4, 4]);
            ctx.strokeStyle = "rgba(255,255,255,0.35)";
            ctx.strokeRect(nx1 * rw, ny1 * rh, Math.max(0, wPx), Math.max(0, hPx));
            ctx.setLineDash([]);
        }
        if (circleStart && lastPtrNorm && (LASER_STROKE_TOOLS.has(activeTool) || SOLID_WALL_STROKE_TOOLS.has(activeTool))) {
            const cx = circleStart.nx * rw;
            const cy = circleStart.ny * rh;
            const px = lastPtrNorm.nx * rw;
            const py = lastPtrNorm.ny * rh;
            const rad = Math.hypot(px - cx, py - cy);
            ctx.setLineDash([4, 4]);
            ctx.strokeStyle = "rgba(255,255,255,0.4)";
            ctx.beginPath();
            ctx.arc(cx, cy, rad, 0, Math.PI * 2);
            ctx.stroke();
            ctx.setLineDash([]);
        }
        if (windSquareStart && lastPtrNorm && activeTool === "wind") {
            const nx1 = Math.min(windSquareStart.nx, lastPtrNorm.nx);
            const nx2 = Math.max(windSquareStart.nx, lastPtrNorm.nx);
            const ny1 = Math.min(windSquareStart.ny, lastPtrNorm.ny);
            const ny2 = Math.max(windSquareStart.ny, lastPtrNorm.ny);
            const wPx = (nx2 - nx1) * rw;
            const hPx = (ny2 - ny1) * rh;
            ctx.setLineDash([5, 4]);
            ctx.strokeStyle = "rgba(160,235,255,0.55)";
            ctx.strokeRect(nx1 * rw, ny1 * rh, Math.max(0, wPx), Math.max(0, hPx));
            ctx.setLineDash([]);
        }
        if (textSquareStart && lastPtrNorm && activeTool === "text") {
            const nx1 = Math.min(textSquareStart.nx, lastPtrNorm.nx);
            const nx2 = Math.max(textSquareStart.nx, lastPtrNorm.nx);
            const ny1 = Math.min(textSquareStart.ny, lastPtrNorm.ny);
            const ny2 = Math.max(textSquareStart.ny, lastPtrNorm.ny);
            const wPx = (nx2 - nx1) * rw;
            const hPx = (ny2 - ny1) * rh;
            ctx.setLineDash([5, 4]);
            ctx.strokeStyle = "rgba(255,255,255,0.45)";
            ctx.strokeRect(nx1 * rw, ny1 * rh, Math.max(0, wPx), Math.max(0, hPx));
            ctx.setLineDash([]);
        }
        if (selectPointerMode === "box" && selectMarqueeAnchor && lastPtrNorm && activeTool === "select") {
            const rx = normRectFromCorners(
                selectMarqueeAnchor.nx,
                selectMarqueeAnchor.ny,
                lastPtrNorm.nx,
                lastPtrNorm.ny,
            );
            ctx.setLineDash([5, 4]);
            ctx.strokeStyle = "rgba(120,220,255,0.9)";
            ctx.strokeRect(
                rx.x1 * rw,
                rx.y1 * rh,
                (rx.x2 - rx.x1) * rw,
                (rx.y2 - rx.y1) * rh,
            );
            ctx.setLineDash([]);
        }
        if (!hidePaths && movePathDraft && movePathDraft.anchors.length) {
            const R = movePathDraft.groupRest;
            const pts = [R, ...movePathDraft.anchors];
            ctx.strokeStyle = "rgba(255,255,120,0.95)";
            ctx.lineWidth = 2;
            ctx.setLineDash([6, 4]);
            ctx.beginPath();
            ctx.moveTo(pts[0].nx * rw, pts[0].ny * rh);
            for (let pi = 1; pi < pts.length; pi++) ctx.lineTo(pts[pi].nx * rw, pts[pi].ny * rh);
            ctx.stroke();
            ctx.setLineDash([]);
        }
        syncAnimPanelVisibility();
        syncPathDraftUi();
        syncMoveSpeedUiFromSelection();
        syncEntitySizeRow();
        syncLaserWidthRow();
        rowSelectLaserOpts.classList.toggle(
            "hidden",
            !(activeTool === "select" && selectionHasLaserTargets()),
        );
        syncWindDirPanelFromSelection();
        syncTextSidePanelFromSelection();
        const showDeleteObject = [...selectedKeys].some((k) => k !== "goal" && k !== "spawn");
        deleteObjectBtn.classList.toggle("hidden", !showDeleteObject);
    }

    function openPublishDialog() {
        void (async () => {
            const levels = await fetchPenaltyLevels();
            const indices = levels.map((l) => l.levelIndex).sort((a, b) => a - b);
            const next = indices.length ? Math.max(...indices) + 1 : 1;

            const wrap = document.createElement("div");
            wrap.className = "adminSaveDialogOverlay";
            const box = document.createElement("div");
            box.className = "adminSaveDialog modeBox";
            box.innerHTML = "<h3>Publish Penalty level</h3>";
            const hint = document.createElement("p");
            hint.className = "accessHint";
            hint.textContent = "Publish makes this layout official for all players.";
            box.appendChild(hint);
            const sel = document.createElement("select");
            sel.className = "formInput";
            const oNew = document.createElement("option");
            oNew.value = "__new__";
            oNew.textContent = canPublish ? `Add new level (→ ${next})` : `Add new level (→ ${next}) — needs Publish permission`;
            oNew.disabled = !canPublish;
            sel.appendChild(oNew);
            for (const idx of indices) {
                const o = document.createElement("option");
                o.value = String(idx);
                o.textContent = canOverwrite ? `Level ${idx} (overwrite)` : `Level ${idx} (overwrite — needs Overwrite permission)`;
                o.disabled = !canOverwrite;
                sel.appendChild(o);
            }
            const row = document.createElement("div");
            row.className = "formRow";
            row.appendChild(sel);
            box.appendChild(row);
            const btnRow = document.createElement("div");
            btnRow.className = "adminSaveBtns";
            const cancel = document.createElement("button");
            cancel.type = "button";
            cancel.className = "btn";
            cancel.textContent = "Cancel";
            const ok = document.createElement("button");
            ok.type = "button";
            ok.className = "btn green";
            ok.textContent = "Publish";
            btnRow.appendChild(cancel);
            btnRow.appendChild(ok);
            box.appendChild(btnRow);
            wrap.appendChild(box);
            document.body.appendChild(wrap);

            const close = () => wrap.remove();

            cancel.onclick = close;
            ok.onclick = () => {
                const v = sel.value;
                let idx = next;
                if (v !== "__new__") {
                    if (!canOverwrite) {
                        alert("You need Overwrite Levels permission to replace an official level.");
                        return;
                    }
                    idx = Math.floor(Number(v));
                    if (!confirm("Overwrite official level " + idx + "?")) return;
                } else if (!canPublish) {
                    alert("You need Publish Penalty Levels permission to add a new official level.");
                    return;
                }
                void (async () => {
                    try {
                        await saveAdminPenaltyLevel(idx, levelToJson(def));
                        close();
                        opts.onLevelsChanged();
                    } catch (e) {
                        const msg = e instanceof Error ? e.message : "";
                        if (msg.includes("publish_forbidden") || msg.includes("overwrite_forbidden")) {
                            alert("Publish blocked: missing administrator permission.");
                        } else {
                            alert("Could not publish level (check admin session and database).");
                        }
                    }
                })();
            };
        })();
    }

    function formatDraftWhen(iso: string) {
        try {
            return new Date(iso).toLocaleString();
        } catch {
            return iso;
        }
    }

    function openSaveDraftDialog() {
        const wrap = document.createElement("div");
        wrap.className = "adminSaveDialogOverlay";
        const box = document.createElement("div");
        box.className = "adminSaveDialog modeBox";
        box.innerHTML = "<h3>Save draft</h3>";
        const hint = document.createElement("p");
        hint.className = "accessHint";
        hint.textContent = "Shared with everyone who has Penalty Editor access.";
        box.appendChild(hint);
        const nameInput = document.createElement("input");
        nameInput.type = "text";
        nameInput.className = "formInput";
        nameInput.placeholder = "Optional name (date used if empty)";
        nameInput.maxLength = 120;
        const nameRow = document.createElement("div");
        nameRow.className = "formRow";
        nameRow.appendChild(nameInput);
        box.appendChild(nameRow);
        const btnRow = document.createElement("div");
        btnRow.className = "adminSaveBtns";
        const cancel = document.createElement("button");
        cancel.type = "button";
        cancel.className = "btn";
        cancel.textContent = "Cancel";
        const ok = document.createElement("button");
        ok.type = "button";
        ok.className = "btn green";
        ok.textContent = "Save";
        btnRow.appendChild(cancel);
        btnRow.appendChild(ok);
        box.appendChild(btnRow);
        wrap.appendChild(box);
        document.body.appendChild(wrap);
        const close = () => wrap.remove();
        cancel.onclick = close;
        ok.onclick = () => {
            void (async () => {
                try {
                    const name = nameInput.value.trim() || null;
                    await savePenaltyEditorDraft(levelToJson(def), name);
                    close();
                } catch {
                    alert("Could not save draft (check session and database).");
                }
            })();
        };
    }

    function openLoadDraftDialog() {
        void (async () => {
            let drafts: PenaltyEditorDraftSummary[] = [];
            try {
                drafts = await fetchPenaltyEditorDrafts();
            } catch {
                alert("Could not load draft list.");
                return;
            }
            const wrap = document.createElement("div");
            wrap.className = "adminSaveDialogOverlay";
            const box = document.createElement("div");
            box.className = "adminSaveDialog modeBox adminLoadDraftDialog";
            box.innerHTML = "<h3>Load draft</h3>";
            const close = () => wrap.remove();
            box.prepend(createModalCloseButton(close, "Close load draft"));
            if (!drafts.length) {
                const empty = document.createElement("p");
                empty.className = "accessHint";
                empty.textContent = "No saved drafts yet.";
                box.appendChild(empty);
            } else {
                const list = document.createElement("div");
                list.className = "adminDraftList";
                for (const d of drafts) {
                    const row = document.createElement("button");
                    row.type = "button";
                    row.className = "btn adminDraftListItem";
                    const label = d.name?.trim() || "(untitled)";
                    const who = d.authorDisplayName || "Unknown";
                    row.textContent = `${label} · ${formatDraftWhen(d.createdAt)} · ${who}`;
                    row.onclick = () => {
                        void (async () => {
                            try {
                                const full = await fetchPenaltyEditorDraft(d.id);
                                applyLoadedLevel(normalizePenaltyLevelData(full.data));
                                close();
                            } catch {
                                alert("Could not load draft.");
                            }
                        })();
                    };
                    list.appendChild(row);
                }
                box.appendChild(list);
            }
            wrap.appendChild(box);
            document.body.appendChild(wrap);
        })();
    }

    let rotHistTimer: number | null = null;
    function scheduleRotHistory() {
        if (rotHistTimer !== null) window.clearTimeout(rotHistTimer);
        rotHistTimer = window.setTimeout(() => {
            rotHistTimer = null;
            maybeRecordHistory();
        }, 350);
    }

    function syncRotInfiniteEditorUi() {
        const inf = admRotInfinite.checked;
        admRotDirRow.classList.toggle("hidden", !inf);
        rotDegRow.classList.toggle("hidden", inf);
    }

    function loadRotUiFromSeg(seg: WallSegDef) {
        const r = seg.rotate;
        admRotPeriod.value = r ? String(r.periodS) : "3";
        admRotDeg.value = r ? String(r.deg) : "90";
        admRotDegSlider.value = String(clamp(Number(admRotDeg.value) || 0, -180, 180));
        admRotInfinite.checked = !!(r && r.infinite);
        if (r?.infinite) {
            admRotRight.checked = r.spinDir === -1;
            admRotLeft.checked = r.spinDir === 1;
        } else {
            admRotRight.checked = false;
            admRotLeft.checked = false;
        }
        syncRotInfiniteEditorUi();
    }

    function applyRotToSelectedSegs() {
        if (!rotateWallsEnabled) return;
        const periodS = clamp(Number(admRotPeriod.value || 3), 0.05, 120);
        const deg = clamp(Number(admRotDeg.value) || 0, -720, 720);
        let rotateSpec: WallSegDef["rotate"];
        if (admRotInfinite.checked) {
            if (!admRotRight.checked && !admRotLeft.checked) admRotLeft.checked = true;
            const spinDir = admRotRight.checked ? -1 : 1;
            rotateSpec = { deg, periodS, infinite: true, spinDir };
        } else {
            rotateSpec = { deg, periodS };
        }
        const segIndices: number[] =
            activeTool === "select"
                ? [...selectedKeys]
                      .filter((k) => k.startsWith("seg:"))
                      .map((k) => Number(k.slice(4)))
                      .filter((i) => i >= 0 && i < def.staticSegs.length)
                : selectedSeg >= 0
                  ? [selectedSeg]
                  : [];
        if (!segIndices.length) return;
        for (const i of segIndices) {
            def.staticSegs[i].rotate = rotateSpec ? { ...rotateSpec } : undefined;
        }
        paint();
        scheduleRotHistory();
    }

    function bindRotControls() {
        const syncSlider = () => {
            admRotDegSlider.value = String(clamp(Number(admRotDeg.value) || 0, -180, 180));
        };
        admRotDegSlider.addEventListener("input", () => {
            admRotDeg.value = admRotDegSlider.value;
            applyRotToSelectedSegs();
        });
        admRotDeg.addEventListener("input", () => {
            syncSlider();
            applyRotToSelectedSegs();
        });
        admRotPeriod.addEventListener("change", () => {
            applyRotToSelectedSegs();
        });
        admRotInfinite.addEventListener("change", () => {
            if (admRotInfinite.checked) {
                if (!admRotRight.checked && !admRotLeft.checked) admRotLeft.checked = true;
            }
            syncRotInfiniteEditorUi();
            applyRotToSelectedSegs();
        });
        admRotRight.addEventListener("change", () => {
            if (admRotRight.checked) admRotLeft.checked = false;
            if (!admRotRight.checked && !admRotLeft.checked) admRotLeft.checked = true;
            applyRotToSelectedSegs();
        });
        admRotLeft.addEventListener("change", () => {
            if (admRotLeft.checked) admRotRight.checked = false;
            if (!admRotRight.checked && !admRotLeft.checked) admRotRight.checked = true;
            applyRotToSelectedSegs();
        });
        const onMoveSpeedFieldInput = () => {
            if (moveSpeedUiSyncing) return;
            if (applyMoveSpeedEditFromInputs()) {
                paint();
                scheduleMoveSpeedHistory();
            }
        };
        admMoveOut.addEventListener("input", onMoveSpeedFieldInput);
        admMoveBack.addEventListener("input", onMoveSpeedFieldInput);
        admMoveOut.addEventListener("change", () => {
            if (moveSpeedUiSyncing) return;
            if (!getMoveSpeedEditTarget()) maybeRecordHistory();
        });
        admMoveBack.addEventListener("change", () => {
            if (moveSpeedUiSyncing) return;
            if (!getMoveSpeedEditTarget()) maybeRecordHistory();
        });
        syncSlider();
        syncRotInfiniteEditorUi();
    }

    function stampNewLaserLineHalf(w: WallSegDef) {
        const m = clamp(laserDrawWidthMul, 0.25, 5);
        if (Math.abs(m - 1) < 0.0001) delete w.lineHalfMul;
        else w.lineHalfMul = m;
    }

    function addSeg(a: WallSegDef) {
        if (!a.id) a.id = uid();
        stampNewLaserLineHalf(a);
        def.staticSegs.push(a);
    }

    function addCircleSegs(cx: number, cy: number, r: number, solidWall: boolean) {
        if (r < 0.006) return;
        const n = 28;
        for (let i = 0; i < n; i++) {
            const a0 = (i / n) * Math.PI * 2;
            const a1 = ((i + 1) / n) * Math.PI * 2;
            const seg: WallSegDef = {
                ax: clamp(cx + Math.cos(a0) * r, 0, 1),
                ay: clamp(cy + Math.sin(a0) * r, 0, 1),
                bx: clamp(cx + Math.cos(a1) * r, 0, 1),
                by: clamp(cy + Math.sin(a1) * r, 0, 1),
            };
            if (solidWall) seg.solidWall = true;
            addSeg(seg);
        }
    }

    function pruneSegSelectionAfterRemove(removedIdx: number) {
        const next = new Set<SelKey>();
        for (const k of selectedKeys) {
            if (k.startsWith("seg:")) {
                const i = Number(k.slice(4));
                if (i === removedIdx) continue;
                if (i > removedIdx) next.add(`seg:${i - 1}`);
                else next.add(k);
            } else next.add(k);
        }
        selectedKeys.clear();
        for (const k of next) selectedKeys.add(k);
    }

    function pruneWindSelectionAfterRemove(removedIdx: number) {
        const next = new Set<SelKey>();
        for (const k of selectedKeys) {
            if (k.startsWith("wind:")) {
                const i = Number(k.slice(5));
                if (i === removedIdx) continue;
                if (i > removedIdx) next.add(`wind:${i - 1}`);
                else next.add(k);
            } else next.add(k);
        }
        selectedKeys.clear();
        for (const k of next) selectedKeys.add(k);
    }

    function pruneTextSelectionAfterRemove(removedIdx: number) {
        const next = new Set<SelKey>();
        for (const k of selectedKeys) {
            if (k.startsWith("txt:")) {
                const i = Number(k.slice(4));
                if (i === removedIdx) continue;
                if (i > removedIdx) next.add(`txt:${i - 1}`);
                else next.add(k);
            } else next.add(k);
        }
        selectedKeys.clear();
        for (const k of next) selectedKeys.add(k);
    }

    function pruneMvSelectionAfterRemove(removedIdx: number) {
        const next = new Set<SelKey>();
        for (const k of selectedKeys) {
            if (k.startsWith("mv:")) {
                const i = Number(k.slice(3));
                if (i === removedIdx) continue;
                if (i > removedIdx) next.add(`mv:${i - 1}`);
                else next.add(k);
            } else next.add(k);
        }
        selectedKeys.clear();
        for (const k of next) selectedKeys.add(k);
    }

    function prunePtSelectionAfterRemove(removedIdx: number) {
        const next = new Set<SelKey>();
        for (const k of selectedKeys) {
            if (k.startsWith("pt:")) {
                const i = Number(k.slice(3));
                if (i === removedIdx) continue;
                if (i > removedIdx) next.add(`pt:${i - 1}`);
                else next.add(k);
            } else next.add(k);
        }
        selectedKeys.clear();
        for (const k of next) selectedKeys.add(k);
    }

    function pruneCkSelectionAfterRemove(removedIdx: number) {
        const next = new Set<SelKey>();
        for (const k of selectedKeys) {
            if (k.startsWith("ck:")) {
                const i = Number(k.slice(3));
                if (i === removedIdx) continue;
                if (i > removedIdx) next.add(ckKey(i - 1));
                else next.add(k);
            } else next.add(k);
        }
        selectedKeys.clear();
        for (const k of next) selectedKeys.add(k);
    }

    function prunePortalSelectionAfterRemove(removedIdx: number) {
        const next = new Set<SelKey>();
        for (const k of selectedKeys) {
            if (k.startsWith("portal:")) {
                const i = Number(k.slice(7));
                if (i === removedIdx) continue;
                if (i > removedIdx) next.add(portalKey(i - 1));
                else next.add(k);
            } else next.add(k);
        }
        selectedKeys.clear();
        for (const k of next) selectedKeys.add(k);
    }

    function deleteSelectedDeletableObjects() {
        const keys = [...selectedKeys].filter((k) => k !== "goal" && k !== "spawn");
        if (!keys.length) return;

        const segIdx: number[] = [];
        const mvIdx: number[] = [];
        const ptIdx: number[] = [];
        const windIdx: number[] = [];
        const textIdx: number[] = [];
        const ckIdx: number[] = [];
        const portalIdx: number[] = [];
        let delTspawn = false;
        for (const k of keys) {
            if (k === "tspawn") delTspawn = true;
            else if (k.startsWith("ck:")) ckIdx.push(Number(k.slice(3)));
            else if (k.startsWith("portal:")) portalIdx.push(Number(k.slice(7)));
            else if (k.startsWith("seg:")) segIdx.push(Number(k.slice(4)));
            else if (k.startsWith("mv:")) mvIdx.push(Number(k.slice(3)));
            else if (k.startsWith("pt:")) ptIdx.push(Number(k.slice(3)));
            else if (k.startsWith("wind:")) windIdx.push(Number(k.slice(5)));
            else if (k.startsWith("txt:")) textIdx.push(Number(k.slice(4)));
        }

        if (delTspawn) {
            delete def.temporalSpawn;
            selectedKeys.delete("tspawn");
        }

        const uniqSortDesc = (arr: number[]) =>
            [...new Set(arr.filter((i) => Number.isFinite(i)))].sort((a, b) => b - a);

        for (const i of uniqSortDesc(ckIdx)) {
            const cks = ensureCheckpoints(def);
            if (i >= 0 && i < cks.length) {
                cks.splice(i, 1);
                pruneCkSelectionAfterRemove(i);
            }
        }
        if (def.checkpoints && !def.checkpoints.length) delete def.checkpoints;

        for (const i of uniqSortDesc(portalIdx)) {
            if (def.portals && i >= 0 && i < def.portals.length) {
                def.portals.splice(i, 1);
                prunePortalSelectionAfterRemove(i);
            }
        }
        if (def.portals && !def.portals.length) delete def.portals;

        for (const i of uniqSortDesc(segIdx)) {
            if (i >= 0 && i < def.staticSegs.length) {
                def.staticSegs.splice(i, 1);
                pruneSegSelectionAfterRemove(i);
                if (selectedSeg === i) selectedSeg = -1;
                else if (selectedSeg > i) selectedSeg--;
            }
        }
        for (const i of uniqSortDesc(mvIdx)) {
            if (i >= 0 && i < def.moving.length) {
                def.moving.splice(i, 1);
                pruneMvSelectionAfterRemove(i);
            }
        }
        for (const i of uniqSortDesc(ptIdx)) {
            if (def.points && i >= 0 && i < def.points.length) {
                def.points.splice(i, 1);
                prunePtSelectionAfterRemove(i);
            }
        }
        if (def.points && !def.points.length) delete def.points;

        for (const i of uniqSortDesc(windIdx)) {
            if (def.winds && i >= 0 && i < def.winds.length) {
                def.winds.splice(i, 1);
                pruneWindSelectionAfterRemove(i);
            }
        }
        if (def.winds && !def.winds.length) delete def.winds;

        for (const i of uniqSortDesc(textIdx)) {
            if (def.texts && i >= 0 && i < def.texts.length) {
                def.texts.splice(i, 1);
                pruneTextSelectionAfterRemove(i);
            }
        }
        if (def.texts && !def.texts.length) delete def.texts;

        selectionRotateMode = false;
        textRotateBtn.classList.remove("green");
        objectRotateBtn.classList.remove("green");
        pendingMovePick = null;
        movePathDraft = null;
        syncPathDraftUi();
        if (selectedSeg >= def.staticSegs.length) selectedSeg = -1;
        paint();
        maybeRecordHistory();
    }

    deleteObjectBtn.onclick = () => deleteSelectedDeletableObjects();

    function eraseAt(nx: number, ny: number) {
        const tErase = hitTextNorm(nx, ny);
        if (tErase >= 0 && def.texts) {
            def.texts.splice(tErase, 1);
            if (!def.texts.length) delete def.texts;
            pruneTextSelectionAfterRemove(tErase);
            return;
        }
        const wErase = hitWindNorm(nx, ny);
        if (wErase >= 0 && def.winds) {
            def.winds.splice(wErase, 1);
            if (!def.winds.length) delete def.winds;
            pruneWindSelectionAfterRemove(wErase);
            return;
        }
        const pErase = hitPortalNorm(nx, ny);
        if (pErase >= 0 && def.portals) {
            def.portals.splice(pErase, 1);
            if (!def.portals.length) delete def.portals;
            prunePortalSelectionAfterRemove(pErase);
            return;
        }
        const idx = hitSeg(nx, ny);
        if (idx >= 0) {
            def.staticSegs.splice(idx, 1);
            pruneSegSelectionAfterRemove(idx);
            if (selectedSeg === idx) selectedSeg = -1;
            else if (selectedSeg > idx) selectedSeg--;
        }
    }

    canvas.addEventListener("pointermove", (e) => {
        const rect = canvas.getBoundingClientRect();
        const nx = clamp((e.clientX - rect.left) / rect.width, 0, 1);
        const ny = clamp((e.clientY - rect.top) / rect.height, 0, 1);
        lastPtrNorm = { nx, ny };

        if (activeTool === "wind" && windSquareStart && e.buttons === 1) {
            paint();
            return;
        }
        if (activeTool === "text" && textSquareStart && e.buttons === 1) {
            paint();
            return;
        }
        if (selectionRotateDrag != null) {
            const dpr = Math.min(2, window.devicePixelRatio || 1);
            const rw = canvas.width / dpr;
            const rh = canvas.height / dpr;
            const tAnim = pauseAnimationsCheck.checked ? 0 : performance.now() / 1000;
            const h = rotateTargetHandlePx(selectionRotateDrag, rw, rh, tAnim);
            const px = nx * rw;
            const py = ny * rh;
            const ang = Math.atan2(py - h.cy, px - h.cx) + Math.PI / 2;
            setTargetRotationDeg(selectionRotateDrag, (ang * 180) / Math.PI);
            paint();
            return;
        }

        if (entityDrag === "spawn" && activeTool === "spawn" && e.buttons === 1) {
            def.spawn.nx = nx;
            def.spawn.ny = ny;
            paint();
            return;
        }
        if (entityDrag === "ballSpawn" && activeTool === "ballSpawn" && e.buttons === 1) {
            def.ballSpawn.nx = nx;
            def.ballSpawn.ny = ny;
            paint();
            return;
        }
        if (entityDrag === "goal" && activeTool === "goal" && e.buttons === 1) {
            def.goal.nx = nx;
            def.goal.ny = ny;
            paint();
            return;
        }
        if (bulkDrag && activeTool === "select" && e.buttons === 1) {
            applyBulkDelta(nx - bulkDrag.startNx, ny - bulkDrag.startNy, bulkDrag.snap);
            paint();
            return;
        }

        if (activeTool === "select" && selectMarqueeAnchor && e.buttons === 1) {
            const ax = selectMarqueeAnchor.nx;
            const ay = selectMarqueeAnchor.ny;
            const d = Math.hypot(nx - ax, ny - ay);
            if (selectPointerMode === null && d > 0.008) {
                if (selectMarqueePick && selectedKeys.has(selectMarqueePick)) {
                    selectPointerMode = "move";
                    bulkDrag = {
                        startNx: ax,
                        startNy: ay,
                        snap: captureBulkSnap(selectedKeys),
                    };
                } else {
                    selectPointerMode = "box";
                }
            }
            if (selectPointerMode === "move" && bulkDrag) {
                applyBulkDelta(nx - bulkDrag.startNx, ny - bulkDrag.startNy, bulkDrag.snap);
                paint();
                return;
            }
            paint();
            return;
        }

        if ((activeTool === "paint" || activeTool === "paintWall") && paintLast && e.buttons === 1) {
            if (Math.hypot(nx - paintLast.nx, ny - paintLast.ny) > 0.008) {
                const stroke: WallSegDef = {
                    ax: paintLast.nx,
                    ay: paintLast.ny,
                    bx: nx,
                    by: ny,
                };
                if (activeTool === "paintWall") stroke.solidWall = true;
                addSeg(stroke);
                paintLast = { nx, ny };
            }
        } else if (activeTool === "erase" && e.buttons === 1) {
            eraseAt(nx, ny);
        }

        paint();
    });

    canvas.addEventListener("pointerdown", (e) => {
        const rect = canvas.getBoundingClientRect();
        const nx = clamp((e.clientX - rect.left) / rect.width, 0, 1);
        const ny = clamp((e.clientY - rect.top) / rect.height, 0, 1);
        lastPtrNorm = { nx, ny };
        canvas.setPointerCapture(e.pointerId);

        const tPick = pauseAnimationsCheck.checked ? 0 : performance.now() / 1000;

        if (activeTool === "spawn") {
            if (hitSpawnNorm(nx, ny)) entityDrag = "spawn";
            else {
                def.spawn.nx = nx;
                def.spawn.ny = ny;
            }
            paint();
            return;
        }
        if (activeTool === "ballSpawn") {
            if (hitBallSpawnNorm(nx, ny)) entityDrag = "ballSpawn";
            else {
                def.ballSpawn.nx = nx;
                def.ballSpawn.ny = ny;
            }
            paint();
            return;
        }
        if (activeTool === "goal") {
            if (hitGoalNorm(nx, ny)) entityDrag = "goal";
            else {
                def.goal.nx = nx;
                def.goal.ny = ny;
            }
            paint();
            return;
        }
        if (activeTool === "select") {
            const dprPick = Math.min(2, window.devicePixelRatio || 1);
            const rwPick = canvas.width / dprPick;
            const rhPick = canvas.height / dprPick;
            if (selectionRotateMode) {
                const rt = hitSelectionRotateHandle(nx, ny, rwPick, rhPick, tPick);
                if (rt) {
                    selectionRotateDrag = rt;
                    paint();
                    return;
                }
            }
            if (moveWallsEnabled && selectedKeys.size > 0) {
                const pickEarly = pickSelectableAt(nx, ny, tPick);
                if (pickEarly && movePathDraft) {
                    clearMovePathDraft();
                    syncPathDraftUi();
                }
                if (!pickEarly) {
                    pendingMovePick = null;
                    const keys = [...selectedKeys].filter((k) => k !== "spawn");
                    if (keys.length) {
                        if (!movePathDraft) {
                            const cen = computeSelectionCentroidNorm(keys);
                            if (cen) {
                                movePathDraft = {
                                    groupRest: { ...cen },
                                    anchors: [],
                                    targetKeys: keys,
                                    fromSelection: true,
                                };
                            }
                        }
                        if (movePathDraft) {
                            movePathDraft.anchors.push({ nx: clamp(nx, 0, 1), ny: clamp(ny, 0, 1) });
                            syncPathDraftUi();
                        }
                    }
                    paint();
                    return;
                }
            }
            selectMarqueeAnchor = { nx, ny };
            selectMarqueePick = pickSelectableAt(nx, ny, tPick);
            selectPointerMode = null;
            paint();
            return;
        }

        if (moveWallsEnabled && pendingMovePick && !rotateWallsEnabled && (!movePathDraft || !movePathDraft.fromSelection)) {
            const s = pendingMovePick;
            const idx = def.staticSegs.indexOf(s);
            if (idx >= 0) {
                const pickSeg = hitSeg(nx, ny);
                const mx = (s.ax + s.bx) / 2;
                const my = (s.ay + s.by) / 2;
                if (!movePathDraft) {
                    if (pickSeg === idx) {
                        paint();
                        return;
                    }
                    movePathDraft = {
                        groupRest: { nx: mx, ny: my },
                        anchors: [{ nx: clamp(nx, 0, 1), ny: clamp(ny, 0, 1) }],
                        targetKeys: [`seg:${idx}`],
                        fromSelection: false,
                    };
                    syncPathDraftUi();
                    paint();
                    return;
                }
                if (movePathDraft.targetKeys.length === 1 && movePathDraft.targetKeys[0] === `seg:${idx}`) {
                    if (pickSeg !== idx) {
                        movePathDraft.anchors.push({ nx: clamp(nx, 0, 1), ny: clamp(ny, 0, 1) });
                    }
                    syncPathDraftUi();
                    paint();
                    return;
                }
            }
        }

        if (moveWallsEnabled && !movePathDraft) {
            const hi = hitSeg(nx, ny);
            if (hi >= 0) {
                selectedSeg = hi;
                pendingMovePick = def.staticSegs[hi];
                paint();
                return;
            }
        }
        if (rotateWallsEnabled) {
            const hi = hitSeg(nx, ny);
            if (hi >= 0) {
                selectedSeg = hi;
                loadRotUiFromSeg(def.staticSegs[hi]);
                applyRotToSelectedSegs();
                paint();
                return;
            }
        }

        if (activeTool === "points") {
            if (toggleWallsCheck.checked) {
                const gv = Math.floor(Number(pointsGroupSelect.value));
                if (!Number.isFinite(gv) || gv < 1 || gv > 15) {
                    alert("Choose group 1–15 to assign a toggle laser.");
                    paint();
                    return;
                }
                const hi = hitSeg(nx, ny);
                if (hi >= 0) {
                    const ptType = readPointTypeSelect(pointsTypeSelect);
                    def.staticSegs[hi].toggleGroup = gv as SpeedrunToggleGroup;
                    applyTogglePointType(def.staticSegs[hi], ptType);
                    paint();
                    maybeRecordHistory();
                }
                return;
            }
            if (!def.points) def.points = [];
            const g2 = Math.floor(Number(pointsGroupSelect.value));
            const ptType = readPointTypeSelect(pointsTypeSelect);
            const p: { nx: number; ny: number; group?: SpeedrunToggleGroup; pointType?: SpeedrunPointType } = { nx, ny };
            if (Number.isFinite(g2) && g2 >= 1 && g2 <= 15) p.group = g2 as SpeedrunToggleGroup;
            if (ptType !== "circle") p.pointType = ptType;
            def.points.push(p);
        } else if (activeTool === "checkpoint") {
            const cks = ensureCheckpoints(def);
            cks.push({ nx, ny });
            activeTool = "select";
            selectedKeys.clear();
            selectedKeys.add(ckKey(cks.length - 1));
            syncToolDropdown();
        } else if (activeTool === "temporalSpawn") {
            def.temporalSpawn = { nx, ny };
            activeTool = "select";
            selectedKeys.clear();
            selectedKeys.add("tspawn");
            syncToolDropdown();
        } else if (activeTool === "portal") {
            portalDragStart = { nx, ny };
        } else if (activeTool === "draw" || activeTool === "drawWall") {
            dragLineStart = { nx, ny };
        } else if (activeTool === "paint" || activeTool === "paintWall") {
            paintLast = { nx, ny };
        } else if (activeTool === "square" || activeTool === "squareWall") {
            squareStart = { nx, ny };
        } else if (activeTool === "circle" || activeTool === "circleWall") {
            circleStart = { nx, ny };
        } else if (activeTool === "wind") {
            windSquareStart = { nx, ny };
        } else if (activeTool === "text") {
            textSquareStart = { nx, ny };
        } else if (activeTool === "erase") {
            eraseAt(nx, ny);
        }

        if (!moveWallsEnabled && !rotateWallsEnabled && activeTool !== "select") {
            const hi = hitSeg(nx, ny);
            selectedSeg = hi;
        }

        paint();
    });

    canvas.addEventListener("pointerup", (e) => {
        const rect = canvas.getBoundingClientRect();
        const nx = clamp((e.clientX - rect.left) / rect.width, 0, 1);
        const ny = clamp((e.clientY - rect.top) / rect.height, 0, 1);

        if (activeTool === "portal" && portalDragStart) {
            const pStart = portalDragStart;
            portalDragStart = null;
            try {
                canvas.releasePointerCapture(e.pointerId);
            } catch {
                /* */
            }
            if (Math.hypot(nx - pStart.nx, ny - pStart.ny) > 0.006) {
                const group = readPortalGroupPick();
                const side = readPortalSidePick();
                upsertPortalLine(group, side, pStart.nx, pStart.ny, nx, ny);
            }
            paint();
            maybeRecordHistory();
            return;
        }

        if (activeTool === "wind" && windSquareStart) {
            const x1 = Math.min(windSquareStart.nx, nx);
            const x2 = Math.max(windSquareStart.nx, nx);
            const y1 = Math.min(windSquareStart.ny, ny);
            const y2 = Math.max(windSquareStart.ny, ny);
            windSquareStart = null;
            try {
                canvas.releasePointerCapture(e.pointerId);
            } catch {
                /* */
            }
            if (x2 - x1 > 0.012 && y2 - y1 > 0.012) {
                if (!def.winds) def.winds = [];
                def.winds.push({ id: uid(), nx: x1, ny: y1, nw: x2 - x1, nh: y2 - y1, dir: "right", strength: 1 });
                activeTool = "select";
                selectedKeys.clear();
                selectedKeys.add(`wind:${def.winds.length - 1}`);
                syncToolDropdown();
            }
            paint();
            maybeRecordHistory();
            return;
        }

        if (activeTool === "text" && textSquareStart) {
            const x1 = Math.min(textSquareStart.nx, nx);
            const x2 = Math.max(textSquareStart.nx, nx);
            const y1 = Math.min(textSquareStart.ny, ny);
            const y2 = Math.max(textSquareStart.ny, ny);
            textSquareStart = null;
            try {
                canvas.releasePointerCapture(e.pointerId);
            } catch {
                /* */
            }
            if (x2 - x1 > 0.012 && y2 - y1 > 0.012) {
                if (!def.texts) def.texts = [];
                def.texts.push({
                    id: `t_${Math.random().toString(36).slice(2, 11)}`,
                    nx: x1,
                    ny: y1,
                    nw: x2 - x1,
                    nh: y2 - y1,
                    content: "",
                    color: "#ffffff",
                    rotationDeg: 0,
                });
                activeTool = "select";
                selectedKeys.clear();
                selectedKeys.add(`txt:${def.texts.length - 1}`);
                syncToolDropdown();
                syncTextSidePanelFromSelection();
                window.setTimeout(() => textContentInput.focus(), 0);
            }
            paint();
            maybeRecordHistory();
            return;
        }

        if (activeTool === "select" && selectMarqueeAnchor) {
            if (bulkDrag) {
                bulkDrag = null;
            } else {
                const ax = selectMarqueeAnchor.nx;
                const ay = selectMarqueeAnchor.ny;
                const d = Math.hypot(nx - ax, ny - ay);
                const tPick = pauseAnimationsCheck.checked ? 0 : performance.now() / 1000;
                if (selectPointerMode === "box" || (selectPointerMode === null && d >= 0.012)) {
                    applyMarqueeSelection(ax, ay, nx, ny, e.shiftKey);
                } else if (d < 0.012) {
                    const pick = pickSelectableAt(ax, ay, tPick);
                    if (e.shiftKey && pick) {
                        if (selectedKeys.has(pick)) selectedKeys.delete(pick);
                        else selectedKeys.add(pick);
                    } else if (pick) {
                        selectedKeys.clear();
                        selectedKeys.add(pick);
                    } else {
                        selectedKeys.clear();
                    }
                }
            }
            applyPendingLaserGroupToSelection();
            syncPortalPickersFromSelection();
            if (movePathDraft?.fromSelection) {
                const ks = new Set(movePathDraft.targetKeys);
                const cur = [...selectedKeys].filter((k) => k !== "spawn");
                const same = cur.length === ks.size && cur.every((k) => ks.has(k));
                if (!same) {
                    clearMovePathDraft();
                    syncPathDraftUi();
                }
            }
            selectMarqueeAnchor = null;
            selectMarqueePick = null;
            selectPointerMode = null;
            try {
                canvas.releasePointerCapture(e.pointerId);
            } catch {
                /* */
            }
            paint();
            maybeRecordHistory();
            return;
        }

        if ((activeTool === "draw" || activeTool === "drawWall") && dragLineStart) {
            if (Math.hypot(nx - dragLineStart.nx, ny - dragLineStart.ny) > 0.006) {
                const seg: WallSegDef = { ax: dragLineStart.nx, ay: dragLineStart.ny, bx: nx, by: ny };
                if (activeTool === "drawWall") seg.solidWall = true;
                addSeg(seg);
            }
            dragLineStart = null;
        }
        if ((activeTool === "square" || activeTool === "squareWall") && squareStart) {
            const x1 = Math.min(squareStart.nx, nx);
            const x2 = Math.max(squareStart.nx, nx);
            const y1 = Math.min(squareStart.ny, ny);
            const y2 = Math.max(squareStart.ny, ny);
            if (x2 - x1 > 0.015 && y2 - y1 > 0.015) {
                const sw = activeTool === "squareWall";
                const mk = (ax: number, ay: number, bx: number, by: number) => {
                    const s: WallSegDef = { ax, ay, bx, by };
                    if (sw) s.solidWall = true;
                    addSeg(s);
                };
                mk(x1, y1, x2, y1);
                mk(x2, y1, x2, y2);
                mk(x2, y2, x1, y2);
                mk(x1, y2, x1, y1);
            }
            squareStart = null;
        }
        if ((activeTool === "circle" || activeTool === "circleWall") && circleStart) {
            const r = Math.hypot(nx - circleStart.nx, ny - circleStart.ny);
            addCircleSegs(circleStart.nx, circleStart.ny, r, activeTool === "circleWall");
            circleStart = null;
        }
        paintLast = null;
        entityDrag = null;
        bulkDrag = null;
        if (selectionRotateDrag != null) {
            selectionRotateDrag = null;
            maybeRecordHistory();
        }
        try {
            canvas.releasePointerCapture(e.pointerId);
        } catch {
            /* */
        }
        paint();
        maybeRecordHistory();
    });

    resetHistoryFromDef();
    bindRotControls();
    syncToolDropdown();
    paint();

    /** When the admin panel is `display:none`, width is 0 — resizing to that wipes the canvas until reload. */
    function fitCanvasToWrap() {
        const w = canvasWrap.clientWidth;
        if (w < 8) return;
        const winW = Math.max(1, window.innerWidth);
        const winH = Math.max(1, window.innerHeight);
        let targetH = Math.floor((w * winH) / winW);
        targetH = Math.max(100, Math.min(targetH, 4800));
        canvas.style.width = "100%";
        canvas.style.height = `${targetH}px`;
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        canvas.width = Math.floor(w * dpr);
        canvas.height = Math.floor(targetH * dpr);
        paint();
    }

    function refitCanvas() {
        requestAnimationFrame(() => {
            fitCanvasToWrap();
            requestAnimationFrame(() => fitCanvasToWrap());
        });
    }

    const ro = new ResizeObserver(() => {
        fitCanvasToWrap();
    });
    ro.observe(canvasWrap);

    let editorDestroyed = false;
    let editorRafId = 0;
    function runEditorAnimLoop() {
        if (editorDestroyed) return;
        paint();
        editorRafId = requestAnimationFrame(runEditorAnimLoop);
    }
    editorRafId = requestAnimationFrame(runEditorAnimLoop);

    function applyLoadedLevel(nextDef: PenaltyLevelDef) {
        def = deepCloneDef(nextDef);
        resetSpeedrunPortalFx();
        selectedSeg = -1;
        pendingMovePick = null;
        movePathDraft = null;
        syncPathDraftUi();
        dragLineStart = null;
        squareStart = null;
        windSquareStart = null;
        textSquareStart = null;
        circleStart = null;
        paintLast = null;
        clearSelectionState();
        selectionRotateMode = false;
        textRotateBtn.classList.remove("green");
        objectRotateBtn.classList.remove("green");
        paint();
        resetHistoryFromDef();
    }

    return {
        destroy() {
            editorDestroyed = true;
            if (sizeHistTimer != null) window.clearTimeout(sizeHistTimer);
            if (windStrengthHistTimer != null) window.clearTimeout(windStrengthHistTimer);
            if (closeToolDropdown) document.removeEventListener("click", closeToolDropdown, true);
            document.removeEventListener("keydown", onEditorKeydown);
            cancelAnimationFrame(editorRafId);
            ro.disconnect();
            mount.replaceChildren();
        },
        loadLevel(nextDef: PenaltyLevelDef) {
            applyLoadedLevel(nextDef);
            refitCanvas();
        },
        refitCanvas,
        getDef(): PenaltyLevelDef {
            return deepCloneDef(def);
        },
    };
}
